import {
  resolvePluginLifecycle,
  validatePluginManifest,
  validateAppManifest,
  resolveAppPluginIds,
  isPluginApiCompatible,
  PLATFORM_PLUGIN_API_VERSION
} from '../../plugin-sdk/src/compat.js';
import { buildCapabilityHelpers } from '../../auth-capabilities/src/index.js';
import {
  applyAppearanceTheme,
  getDefaultAppearanceThemeId,
  getPersistedAppearanceTheme,
  hasPersistedAppearanceTheme,
  isAppearancePack
} from '../../../src/ui/theme_packs.js';
import {
  renderDsButton,
  renderDsMessage,
  renderDsPanel,
  renderDsShellLayout,
  renderDsShellLogout,
  renderDsShellNav,
  renderDsShellStatus,
  renderDsStack
} from '../../../shared-ui/ds/shellColorApi.js';
import {
  applyThemeScope,
  canApplyThemeScope,
  setThemeCapabilityFlags
} from '../../../src/ui/theme_consumers.js';
import {
  getDefaultThemeModeId
} from '../../../src/ui/theme_modes.js';
import {
  getPersistedPlatformThemeMode,
  hasPersistedPlatformThemeMode
} from '../../../src/ui/theme_scope_state.js';
import {
  ensureDashboardTutorVisualParity
} from '../../../src/ui/dashboard_tutor_visual_styles.js';
import {
  registerShellThemeConsumer
} from '../../../src/shell/theme/applyShellThemeMode.js';
import {
  PLATFORM_SHELL_BUSY_OVERLAY_CSS,
  PLATFORM_SHELL_BUSY_OVERLAY_ICON
} from './theme/busy_overlay_theme.js';
import { createTtoriaActivityRegistry } from './activity.js';
import {
  createTtoriaActivityRailController,
  renderTtoriaActivityLaneMarkup
} from './activity_presenter.js';
import {
  TTORIA_ASYNC_FEEDBACK_TARGETS_BY_APP,
  createTtoriaAsyncFeedbackTelemetryBatcher,
  resolveTtoriaAsyncFeedbackConfig
} from '../../ttoria-async-feedback/src/index.js';
import {
  TUTOR_DESK_SAAS_DESTINATION_SKELETON_STYLES,
  normalizeTutorDeskDestinationSurface,
  renderTutorDeskDestinationSkeleton
} from '../../../src/ui/tokens/tutor_desk_saas_final_ui_transition_skeleton_styles.js';
import { installSessionExpiryFetchInterceptor } from './session_expiry.js';
import { createDeclaredPluginAccess } from './declared_plugin_access.js';
import { installScrollIndicators } from '../../../src/ui/scroll_indicators.js';

function safeStorage() {
  try {
    return window.localStorage;
  } catch {
    return {
      getItem() { return null; },
      setItem() {},
      removeItem() {}
    };
  }
}

const SHELL_STYLE_ID = 'platform-shell-runtime-layout-v1';

function getBuildMetadataFromWindow() {
  if (typeof window === 'undefined') return null;
  const value = window.__KG_APP_BUILD_INFO__;
  if (value && typeof value === 'object') {
    const buildNumber = String(value.buildNumber || '').trim();
    const builtAt = String(value.builtAt || '').trim();
    if (buildNumber || builtAt) {
      return { buildNumber, builtAt };
    }
  }
  if (typeof document === 'undefined') return null;
  const bootstrapScript = document.querySelector(
    'script[type="module"][src*="/app-bootstrap.js"], script[type="module"][src*="/assets/app-bootstrap-"]'
  );
  if (!bootstrapScript) return null;
  const src = String(bootstrapScript.getAttribute('src') || '').trim();
  if (!src) return null;
  const queryIndex = src.indexOf('?');
  if (queryIndex < 0) return null;
  const params = new URLSearchParams(src.slice(queryIndex + 1));
  const buildNumber = String(params.get('v') || '').trim();
  if (!/^\d{14}$/.test(buildNumber)) return null;
  const year = Number(buildNumber.slice(0, 4));
  const month = Number(buildNumber.slice(4, 6));
  const day = Number(buildNumber.slice(6, 8));
  const hour = Number(buildNumber.slice(8, 10));
  const minute = Number(buildNumber.slice(10, 12));
  const second = Number(buildNumber.slice(12, 14));
  const builtAtDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const builtAt = Number.isNaN(builtAtDate.getTime()) ? '' : builtAtDate.toISOString();
  return { buildNumber, builtAt };
}

function formatBuildMetadataLine(buildMetadata) {
  if (!buildMetadata) return '';
  const parts = [];
  if (buildMetadata.buildNumber) {
    parts.push(`Build #${buildMetadata.buildNumber}`);
  }
  if (buildMetadata.builtAt) {
    const builtAtDate = new Date(buildMetadata.builtAt);
    const builtAtLabel = Number.isNaN(builtAtDate.getTime())
      ? buildMetadata.builtAt
      : builtAtDate.toLocaleString();
    parts.push(builtAtLabel);
  }
  return parts.join(' | ');
}

function asyncFeedbackDurationBucket(durationMs = 0) {
  const value = Math.max(0, Number(durationMs) || 0);
  if (value < 100) return 'lt-100ms';
  if (value < 1000) return '100-999ms';
  if (value < 10000) return '1-9s';
  if (value < 30000) return '10-29s';
  return '30s-plus';
}

function asyncFeedbackFirstFeedbackBucket(durationMs = null) {
  if (!Number.isFinite(Number(durationMs))) return 'not-observed';
  const value = Math.max(0, Number(durationMs));
  if (value <= 16) return 'same-frame';
  if (value < 100) return 'lt-100ms';
  if (value < 1000) return '100-999ms';
  return 'not-observed';
}

function asyncFeedbackRailDwellBucket(durationMs = 0, shown = false) {
  if (!shown) return 'not-shown';
  const value = Math.max(0, Number(durationMs) || 0);
  if (value < 1000) return 'lt-1s';
  if (value < 10000) return '1-9s';
  if (value < 30000) return '10-29s';
  return '30s-plus';
}

export function shouldSuppressSettingsBuildInfoCard({
  root = typeof document !== 'undefined' ? document.documentElement : null,
  mountEl = null
} = {}) {
  if (root?.getAttribute?.('data-kg-ui-standard') !== 'ttoria-saas-final-ui-v1') return false;
  const enabledSurfaces = String(root.getAttribute('data-kg-ui-surfaces') || '')
    .trim()
    .split(/\s+/u)
    .filter(Boolean);
  if (!enabledSurfaces.includes('settings')) return false;
  return Boolean(mountEl?.querySelector?.(
    '[data-testid="settings-final-ui-app-identity"][data-platform-build-info-owner="true"]'
  ));
}

function reconcileSettingsBuildInfoCard(mountEl, { appName = '', buildMetadataText = '' } = {}) {
  if (typeof document === 'undefined' || !mountEl || typeof mountEl.querySelector !== 'function') return;
  mountEl.querySelectorAll('[data-platform-build-info-card="true"]').forEach((node) => node.remove());
  if (shouldSuppressSettingsBuildInfoCard({ mountEl })) return;
  if (!buildMetadataText) return;
  const shellBody = mountEl.querySelector('.kg-settings-shell-body');
  if (!shellBody) return;
  const card = document.createElement('article');
  card.className = 'platform-shell-layout__settings-build-card';
  card.setAttribute('data-platform-build-info-card', 'true');

  const eyebrow = document.createElement('p');
  eyebrow.className = 'platform-shell-layout__settings-build-eyebrow';
  eyebrow.textContent = `${String(appName || 'App').trim() || 'App'} build`;

  const value = document.createElement('p');
  value.className = 'platform-shell-layout__settings-build-value';
  value.textContent = buildMetadataText;

  card.append(eyebrow, value);
  shellBody.prepend(card);
}

function ensureShellStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SHELL_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SHELL_STYLE_ID;
  style.textContent = `
.ds-shell-layout.platform-shell-layout {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 16px;
  height: 100vh;
  min-height: 0;
  padding: 16px;
  align-items: stretch;
}
.ds-shell-layout.platform-shell-layout > .ds-shell-layout__nav,
.ds-shell-layout.platform-shell-layout > .ds-shell-layout__main {
  min-height: 0;
  border-radius: 14px;
  border: 1px solid var(--shell-border, var(--ds-border-subtle, currentColor));
  background: var(--shell-surface, var(--ds-surface-raised, Canvas));
}
.ds-shell-layout.platform-shell-layout > .ds-shell-layout__nav {
  padding: 12px;
  overflow: auto;
}
.ds-shell-layout.platform-shell-layout > .ds-shell-layout__main {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
}
.platform-shell-layout__nav {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 100%;
}
.platform-shell-layout__nav .ds-shell-nav__brand {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.platform-shell-layout__app-name {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
}
.platform-shell-layout__tenant-brand {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  margin-top: 8px;
  padding: 8px;
  border: 1px solid color-mix(in srgb, var(--td-tenant-brand-primary, currentColor) 32%, transparent);
  border-radius: 10px;
}
.platform-shell-layout__tenant-brand[hidden] { display: none; }
.platform-shell-layout__tenant-brand-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 6px;
}
.platform-shell-layout__tenant-brand-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.platform-shell-layout__tenant-brand-name,
.platform-shell-layout__tenant-brand-attribution {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.platform-shell-layout__tenant-brand-name { font-size: 12px; font-weight: 700; }
.platform-shell-layout__tenant-brand-attribution { font-size: 10px; opacity: 0.78; }
.platform-shell-layout__app-build-info {
  margin: 0;
  font-size: 11px;
  opacity: 0.82;
}
.platform-shell-layout__nav .ds-shell-nav__items {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  min-height: 0;
}
.platform-shell-layout__nav-rail {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  min-height: 0;
}
.platform-shell-layout__nav-items {
  display: flex;
  flex-direction: column;
  gap: var(--shell-nav-items-gap, 6px);
  flex: 1 1 auto;
  min-height: 0;
}
.platform-shell-nav__button,
.platform-shell-logout__button {
  width: 100%;
  justify-content: flex-start !important;
  border-radius: var(--shell-nav-control-radius,999px) !important;
}
.platform-shell-nav__button {
  background: var(--shell-nav-inactive-bg, transparent) !important;
  border-width: var(--shell-nav-inactive-border-width, 1px) !important;
  border-style: var(--shell-nav-inactive-border-style, solid) !important;
  border-color: var(--shell-nav-inactive-border-color, var(--shell-nav-inactive-border, var(--shell-border, var(--ds-border-subtle, currentColor)))) !important;
  border-radius: var(--shell-nav-inactive-radius, var(--shell-nav-control-radius,999px)) !important;
  color: var(--shell-nav-inactive-text, var(--shell-nav-text, inherit)) !important;
}
.platform-shell-nav__button--active {
  background: var(--shell-nav-active-bg, transparent) !important;
  border-width: var(--shell-nav-active-border-width, 1px) !important;
  border-style: var(--shell-nav-active-border-style, solid) !important;
  border-color: var(--shell-nav-active-border-color, var(--shell-nav-active-border, Highlight)) !important;
  border-radius: var(--shell-nav-active-radius, var(--shell-nav-control-radius,999px)) !important;
  color: var(--shell-nav-active-text, var(--shell-nav-text, inherit)) !important;
}
.ds-shell-layout.platform-shell-layout > .ds-shell-layout__main > .ds-shell-layout__status {
  flex: 0 0 auto;
}
.platform-shell-layout__status-shell,
.platform-shell-layout__status-shell .ds-shell-status {
  width: 100%;
}
.platform-shell-layout__status-shell .ds-shell-status {
  display: flex;
  align-items: center;
  min-height: 52px;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid var(--shell-status-border, var(--shell-border, var(--ds-border-subtle, currentColor)));
  background: var(--shell-status-bg, var(--shell-surface, var(--ds-surface-raised, Canvas)));
  color: var(--shell-status-text, inherit);
  box-sizing: border-box;
}
.platform-shell-layout__status-text {
  display: flex;
  align-items: baseline;
  gap: 10px;
  width: 100%;
  min-width: 0;
}
.platform-shell-layout__status-copy {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  min-width: 0;
}
.platform-shell-layout__status-summary {
  font-weight: 700;
}
.platform-shell-layout__status-detail {
  opacity: 0.84;
}
.platform-shell-activity-lane {
  --ttoria-async-rail-track: var(--ds-border-subtle, color-mix(in srgb, currentColor 18%, transparent));
  --ttoria-async-rail-accent: var(--ds-accent, Highlight);
  --ttoria-async-rail-text: var(--shell-status-text, inherit);
  display: grid;
  grid-template-rows: 2px 32px;
  width: 100%;
  min-height: 34px;
  overflow: hidden;
  border-radius: 10px;
  color: var(--ttoria-async-rail-text);
  background: var(--shell-status-bg, var(--shell-surface, var(--ds-surface-raised, Canvas)));
  pointer-events: none;
  box-sizing: border-box;
}
.platform-shell-activity-lane[data-activity-phase="succeeded"] {
  --ttoria-async-rail-accent: var(--ds-success, var(--shell-status-success, Highlight));
}
.platform-shell-activity-lane[data-activity-phase="failed"] {
  --ttoria-async-rail-accent: var(--ds-danger, var(--shell-status-danger, Mark));
}
.platform-shell-activity-lane[hidden] {
  display: none !important;
}
.platform-shell-context-lane {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.platform-shell-context-lane[hidden] {
  display: none !important;
}
.platform-shell-activity-lane__rail {
  position: relative;
  height: 2px;
  overflow: hidden;
  background: var(--ttoria-async-rail-track);
  opacity: 0;
}
.platform-shell-activity-lane[data-activity-visible="true"][data-activity-phase="pending"] .platform-shell-activity-lane__rail {
  opacity: 1;
}
.platform-shell-activity-lane__rail-value {
  position: absolute;
  inset: 0 auto 0 0;
  width: var(--ttoria-async-rail-progress, 34%);
  background: var(--ttoria-async-rail-accent);
  transform-origin: left center;
  animation: ttoriaAsyncRailSweep 1.25s ease-in-out infinite alternate;
}
.platform-shell-activity-lane__rail[aria-valuenow] .platform-shell-activity-lane__rail-value {
  animation: none;
}
.platform-shell-activity-lane__row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  height: 32px;
  padding: 0 10px;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 16px;
}
.platform-shell-activity-lane__indicator {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  display: grid;
  place-items: center;
  border: 1px solid color-mix(in srgb, var(--ttoria-async-rail-accent) 68%, transparent);
  border-radius: 6px;
  background: color-mix(in srgb, var(--ttoria-async-rail-accent) 18%, transparent);
  color: var(--ttoria-async-rail-accent);
  opacity: 0;
}
.platform-shell-activity-lane__indicator::before {
  content: "↻";
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
}
.platform-shell-activity-lane[data-activity-phase="succeeded"] .platform-shell-activity-lane__indicator::before {
  content: "✓";
}
.platform-shell-activity-lane[data-activity-phase="failed"] .platform-shell-activity-lane__indicator::before {
  content: "!";
}
.platform-shell-activity-lane[data-activity-visible="true"] .platform-shell-activity-lane__indicator {
  opacity: 1;
}
.platform-shell-activity-lane__copy {
  flex: 1 1 auto;
  display: grid;
  min-width: 0;
  line-height: 14px;
}
.platform-shell-activity-lane__summary {
  min-width: 0;
  overflow: hidden;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.platform-shell-activity-lane__detail {
  min-width: 0;
  overflow: hidden;
  opacity: 0.78;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.platform-shell-activity-lane__count {
  margin-right: 4px;
}
.platform-shell-activity-lane__badge {
  flex: 0 0 auto;
  padding: 3px 7px;
  border: 1px solid color-mix(in srgb, var(--ttoria-async-rail-accent) 62%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, var(--ttoria-async-rail-accent) 14%, transparent);
  color: var(--ttoria-async-rail-accent);
  font-size: 9px;
  font-weight: 800;
  line-height: 12px;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.platform-shell-activity-a11y {
  position: fixed !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
@keyframes ttoriaAsyncRailSweep {
  from { transform: translateX(-15%); }
  to { transform: translateX(210%); }
}
@media (prefers-reduced-motion: reduce) {
  .platform-shell-activity-lane__rail-value {
    animation: none !important;
    transform: none !important;
  }
}
@media (max-width: 560px) {
  .platform-shell-activity-lane__summary {
    flex: 1 1 auto;
  }
  .platform-shell-activity-lane__detail {
    display: none;
  }
}
${PLATFORM_SHELL_BUSY_OVERLAY_CSS}
@keyframes platformShellBusyOverlayOrbit {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes platformShellBusyOverlayFade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes platformShellBusyOverlayPulse {
  0%, 100% {
    transform: scale(0.9);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.08);
    opacity: 1;
  }
}
@keyframes platformShellBusyOverlayRise {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@keyframes platformShellBusyOverlayProgress {
  0% {
    left: -38%;
  }
  55% {
    left: 24%;
  }
  100% {
    left: 108%;
  }
}
@keyframes platformShellBusyOverlayDot {
  0%, 100% {
    opacity: 0.36;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-2px);
  }
}
.platform-shell-layout__content,
#platformRoot {
  flex: 1 1 auto;
  min-height: 0;
}
.ds-shell-layout.platform-shell-layout > .ds-shell-layout__main > .ds-shell-layout__content {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
.ds-shell-layout.platform-shell-layout > .ds-shell-layout__main > .ds-shell-layout__content.is-switching,
#platformRoot.is-switching {
  pointer-events: none;
}
#platformRoot {
  position: relative;
  overflow: hidden;
}
.platform-shell-plugin-stage {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
}
.platform-shell-plugin-host {
  position: absolute;
  inset: 0;
  min-height: 0;
  overflow: auto;
}
.platform-shell-plugin-host.is-inactive {
  visibility: hidden;
  pointer-events: none;
}
.platform-shell-layout__logout-shell {
  margin-top: auto;
}
.platform-shell-layout__logout-host,
#platformLogout {
  width: 100%;
}
.platform-shell-account-name {
  margin: 0 8px 8px;
  overflow: hidden;
  color: var(--shell-nav-text, inherit);
  font-size: 0.75rem;
  font-weight: 650;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.platform-shell-logout__button {
  background: var(--shell-logout-bg, transparent) !important;
  border-color: var(--shell-logout-border, var(--shell-border, var(--ds-border-subtle, currentColor))) !important;
  color: var(--shell-logout-text, inherit) !important;
}
.platform-shell-layout__settings-build-card {
  margin: 0 0 16px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--shell-border, var(--ds-border-subtle, currentColor)) 78%, transparent);
  background: linear-gradient(180deg, color-mix(in srgb, var(--shell-surface, var(--ds-surface-raised, Canvas)) 92%, white 8%), color-mix(in srgb, var(--shell-surface, var(--ds-surface-raised, Canvas)) 98%, transparent));
  box-shadow: 0 12px 28px color-mix(in srgb, var(--shell-text, CanvasText) 8%, transparent);
}
.platform-shell-layout__settings-build-eyebrow {
  margin: 0 0 4px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--shell-text, CanvasText) 68%, transparent);
}
.platform-shell-layout__settings-build-value {
  margin: 0;
  font-size: 0.94rem;
  font-weight: 600;
  line-height: 1.45;
  color: var(--shell-text, CanvasText);
}
[data-platform-load-retry-notice="true"] .platform-shell-error-panel__title {
  font-size: 16px !important;
  line-height: 22px !important;
}
@media (max-width: 920px) {
  .ds-shell-layout.platform-shell-layout {
    grid-template-columns: 1fr;
    height: auto;
    min-height: 100vh;
  }
  .platform-shell-layout__logout-shell {
    margin-top: 0;
  }
  .platform-shell-busy-overlay {
    padding: 12px;
  }
  .platform-shell-busy-overlay__card {
    width: min(380px, calc(100vw - 24px));
    max-width: calc(100vw - 24px);
  }
}
${TUTOR_DESK_SAAS_DESTINATION_SKELETON_STYLES}
`;
  document.head.appendChild(style);
}

function ensureShellDom() {
  let appName = document.getElementById('platformAppName');
  let appBuildInfo = document.getElementById('platformAppBuildInfo');
  let navHost = document.getElementById('platformNav');
  let statusHost = document.getElementById('platformStatus');
  let activityLaneHost = document.getElementById('platformActivityLane');
  let contextLaneHost = document.getElementById('platformContextLane');
  let activityA11yHost = document.getElementById('platformActivityA11y');
  let root = document.getElementById('platformRoot');
  let logoutHost = document.getElementById('platformLogout');

  if (appName && navHost && statusHost && activityLaneHost && contextLaneHost && activityA11yHost && root && logoutHost) {
    return { appName, appBuildInfo, navHost, statusHost, activityLaneHost, contextLaneHost, activityA11yHost, root, logoutHost };
  }

  ensureShellStyles();
  const mount = document.getElementById('platformShellMount') || document.body;
  mount.innerHTML = renderDsShellLayout({
    className: 'platform-shell-layout',
    navHtml: renderDsShellNav({
      className: 'platform-shell-layout__nav',
      brandHtml: [
        renderDsStack({
          tag: 'h1',
          component: 'ds/shell-app-name',
          className: 'platform-shell-layout__app-name',
          attrs: { id: 'platformAppName' },
          html: 'Platform App'
        }),
        renderDsStack({
          tag: 'p',
          component: 'ds/shell-build-info',
          className: 'platform-shell-layout__app-build-info',
          attrs: { id: 'platformAppBuildInfo', hidden: 'hidden' },
          html: ''
        })
      ].join(''),
      itemsHtml: renderDsStack({
        component: 'ds/shell-nav-items-wrap',
        className: 'platform-shell-layout__nav-rail',
        html: [
          renderDsStack({
            component: 'ds/shell-nav-items',
            className: 'platform-shell-layout__nav-items',
            attrs: { id: 'platformNav' }
          }),
          renderDsShellLogout({
            className: 'platform-shell-layout__logout-shell',
            contentHtml: renderDsStack({
              component: 'ds/shell-logout-host',
              className: 'platform-shell-layout__logout-host',
              attrs: { id: 'platformLogout' }
            })
          })
        ].join('')
      })
    }),
    statusHtml: [
      renderDsShellStatus({
        className: 'platform-shell-layout__status-shell',
        statusHtml: renderDsStack({
          component: 'ds/shell-status-text',
          className: 'platform-shell-layout__status-text',
          attrs: { id: 'platformStatus' },
          html: 'Loading...'
        })
      }),
      renderDsStack({
        component: 'ds/activity-lane',
        className: 'platform-shell-activity-lane',
        attrs: {
          id: 'platformActivityLane',
          'data-activity-visible': 'false',
          'data-activity-phase': 'idle',
          hidden: 'hidden',
          'aria-hidden': 'true'
        }
      }),
      renderDsStack({
        component: 'ds/context-lane',
        className: 'ds-shell-status platform-shell-layout__status-shell platform-shell-context-lane',
        attrs: {
          id: 'platformContextLane',
          hidden: 'hidden',
          'aria-hidden': 'true',
          'data-async-feedback-context-lane': 'true'
        }
      }),
      renderDsStack({
        component: 'ds/activity-status',
        className: 'platform-shell-activity-a11y',
        attrs: { id: 'platformActivityA11y' }
      })
    ].join(''),
    contentHtml: renderDsStack({
      component: 'ds/shell-content',
      className: 'platform-shell-layout__content',
      attrs: { id: 'platformRoot' }
    })
  });

  appName = document.getElementById('platformAppName');
  appBuildInfo = document.getElementById('platformAppBuildInfo');
  navHost = document.getElementById('platformNav');
  statusHost = document.getElementById('platformStatus');
  activityLaneHost = document.getElementById('platformActivityLane');
  contextLaneHost = document.getElementById('platformContextLane');
  activityA11yHost = document.getElementById('platformActivityA11y');
  root = document.getElementById('platformRoot');
  logoutHost = document.getElementById('platformLogout');

  return { appName, appBuildInfo, navHost, statusHost, activityLaneHost, contextLaneHost, activityA11yHost, root, logoutHost };
}

async function configureMobileDrawer({ appManifest, appName, root, presentationMode } = {}) {
  const layout = root?.closest?.('.ds-shell-layout.platform-shell-layout');
  const mobileNavMode = String(appManifest?.shell?.mobileNavMode || 'stacked').trim().toLowerCase() || 'stacked';
  if (layout instanceof HTMLElement) layout.dataset.mobileNavMode = mobileNavMode;
  if (mobileNavMode !== 'drawer') return { close() {}, isOpen: () => false };
  const module = await import('./mobile_drawer.js');
  return module.configureMobileDrawer({ appManifest, appName, root, presentationMode });
}

function ensurePluginStage(rootEl) {
  if (!rootEl) {
    return {
      stageEl: null,
      hostEls: { a: null, b: null },
      activeHostKey: 'a'
    };
  }

  let stageEl = rootEl.querySelector('#platformPluginStage');
  let hostA = rootEl.querySelector('#platformPluginHostA');
  let hostB = rootEl.querySelector('#platformPluginHostB');

  if (!stageEl || !hostA || !hostB) {
    rootEl.innerHTML = renderDsStack({
      component: 'ds/shell-plugin-stage',
      className: 'platform-shell-plugin-stage',
      attrs: { id: 'platformPluginStage' },
      html: [
        renderDsStack({
          component: 'ds/shell-plugin-host',
          className: 'platform-shell-plugin-host',
          attrs: { id: 'platformPluginHostA', 'data-shell-host': 'a' }
        }),
        renderDsStack({
          component: 'ds/shell-plugin-host',
          className: 'platform-shell-plugin-host is-inactive',
          attrs: {
            id: 'platformPluginHostB',
            'data-shell-host': 'b',
            'aria-hidden': 'true'
          }
        })
      ].join('')
    });
    stageEl = rootEl.querySelector('#platformPluginStage');
    hostA = rootEl.querySelector('#platformPluginHostA');
    hostB = rootEl.querySelector('#platformPluginHostB');
  }

  return {
    stageEl,
    hostEls: { a: hostA, b: hostB },
    activeHostKey: 'a'
  };
}

function escapeHtml(value) {
  return String(value || '').replace(/[<>&"']/g, (m) => ({
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;',
    '"': '&quot;',
    "'": '&#39;'
  }[m]));
}

const PLATFORM_SHELL_PAINT_FALLBACK_MS = 250;

function waitForPaint(frames = 1) {
  const totalFrames = Math.max(1, Number(frames) || 1);
  return new Promise((resolve) => {
    let settled = false;
    let fallbackTimerId = null;
    let animationFrameId = null;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (fallbackTimerId != null && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(fallbackTimerId);
      }
      if (animationFrameId != null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(animationFrameId);
      }
      resolve();
    };
    const step = (remaining) => {
      if (settled) return;
      if (remaining <= 0) {
        finish();
        return;
      }
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        animationFrameId = window.requestAnimationFrame(() => {
          animationFrameId = null;
          step(remaining - 1);
        });
        return;
      }
      finish();
    };
    if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
      fallbackTimerId = window.setTimeout(finish, PLATFORM_SHELL_PAINT_FALLBACK_MS);
    }
    step(totalFrames);
  });
}

function renderShellBusyOverlayIcon() {
  return PLATFORM_SHELL_BUSY_OVERLAY_ICON;
}

const BRIEF_STATUS_DECAY_MS = 1800;
const SHELL_BUSY_OVERLAY_DELAY_MS = 1000;
const SHELL_STATUS_TONES = new Set(['neutral', 'ok', 'warn', 'err']);
const SHELL_STATUS_STICKY = new Set(['none', 'brief', 'until-next-action']);
const SHELL_STATUS_OP_CLASSES = new Set([
  'route-transition',
  'user-mutation',
  'foreground-read',
  'background-refresh',
  'legacy'
]);
const SHELL_STATUS_PRIORITY = Object.freeze({
  'background-refresh': 1,
  legacy: 2,
  'foreground-read': 3,
  'user-mutation': 4,
  'route-transition': 5
});

function normalizeStatusText(value = '') {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function normalizeStatusTone(value = '', fallback = 'neutral') {
  const normalized = normalizeStatusText(value).toLowerCase();
  return SHELL_STATUS_TONES.has(normalized) ? normalized : fallback;
}

function normalizeStatusSticky(value = '', fallback = 'none') {
  const normalized = normalizeStatusText(value).toLowerCase();
  return SHELL_STATUS_STICKY.has(normalized) ? normalized : fallback;
}

function normalizeStatusOpClass(value = '', fallback = 'legacy') {
  const normalized = normalizeStatusText(value).toLowerCase();
  return SHELL_STATUS_OP_CLASSES.has(normalized) ? normalized : fallback;
}

function normalizeStatusActionToken(value = '', fallback = 'status') {
  const normalized = normalizeStatusText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
}

function normalizeStatusOverlayDelay(value = null, fallback = null) {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 0) {
    return Math.max(0, Math.round(numeric));
  }
  const fallbackNumeric = Number(fallback);
  if (Number.isFinite(fallbackNumeric) && fallbackNumeric >= 0) {
    return Math.max(0, Math.round(fallbackNumeric));
  }
  return null;
}

function normalizeStatusOverlayMinVisible(value = null, fallback = null) {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 0) {
    return Math.max(0, Math.round(numeric));
  }
  const fallbackNumeric = Number(fallback);
  if (Number.isFinite(fallbackNumeric) && fallbackNumeric >= 0) {
    return Math.max(0, Math.round(fallbackNumeric));
  }
  return null;
}

function normalizeStatusCountdownSeconds(value = null, fallback = null) {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 0) {
    return Math.max(0, Math.min(999, Math.round(numeric)));
  }
  const fallbackNumeric = Number(fallback);
  if (Number.isFinite(fallbackNumeric) && fallbackNumeric >= 0) {
    return Math.max(0, Math.min(999, Math.round(fallbackNumeric)));
  }
  return null;
}

function formatStatusCountdownSeconds(value = 0) {
  return `${Math.max(0, Math.round(Number(value) || 0))}s`;
}

function compareShellStatusEntries(left = null, right = null) {
  const leftEpoch = Number(left?.routeEpoch || 0);
  const rightEpoch = Number(right?.routeEpoch || 0);
  if (leftEpoch !== rightEpoch) return leftEpoch - rightEpoch;
  const leftBusy = left?.busy === true ? 1 : 0;
  const rightBusy = right?.busy === true ? 1 : 0;
  if (leftBusy !== rightBusy) return leftBusy - rightBusy;
  const leftPriority = SHELL_STATUS_PRIORITY[normalizeStatusOpClass(left?.opClass)] || 0;
  const rightPriority = SHELL_STATUS_PRIORITY[normalizeStatusOpClass(right?.opClass)] || 0;
  if (leftPriority !== rightPriority) return leftPriority - rightPriority;
  return Number(left?.statusSeq || 0) - Number(right?.statusSeq || 0);
}

const TTORIA_SAAS_FINAL_UI_STANDARD = 'ttoria-saas-final-ui-v1';
const TTORIA_SAAS_FINAL_UI_APP_ID = 'tutor-desk';
const TTORIA_TOOLTIP_STANDARD = 'ttoria-tooltip-v1';
const TTORIA_TOOLTIP_ELIGIBLE_TARGETS = Object.freeze(['saas-staging', 'saas-production']);
const TTORIA_STUDENT_PORTAL_FINAL_UI_APP_ID = 'student';
const TTORIA_SAAS_FINAL_UI_DISABLE_STORAGE_KEY = 'kg.ui.standard.ttoria-saas-final-ui-v1.disabled';
const TTORIA_SAAS_FINAL_UI_SURFACES = Object.freeze([
  'auth',
  'shell',
  'settings',
  'home',
  'students',
  'assessment',
  'students-portal',
  'calendar',
  'payments',
  'digital-library',
  'ai-quality'
]);
const TTORIA_SAAS_FINAL_UI_SURFACE_SET = new Set(TTORIA_SAAS_FINAL_UI_SURFACES);
const TTORIA_STUDENT_PORTAL_FINAL_UI_SURFACES = Object.freeze([
  'auth',
  'students-portal'
]);
const TTORIA_STUDENT_PORTAL_FINAL_UI_SURFACE_SET = new Set(TTORIA_STUDENT_PORTAL_FINAL_UI_SURFACES);

function resolveFinalUiSurfaceAuthority(appId = '') {
  const normalizedAppId = String(appId || '').trim();
  if (normalizedAppId === TTORIA_SAAS_FINAL_UI_APP_ID) {
    return {
      surfaces: TTORIA_SAAS_FINAL_UI_SURFACES,
      surfaceSet: TTORIA_SAAS_FINAL_UI_SURFACE_SET
    };
  }
  if (normalizedAppId === TTORIA_STUDENT_PORTAL_FINAL_UI_APP_ID) {
    return {
      surfaces: TTORIA_STUDENT_PORTAL_FINAL_UI_SURFACES,
      surfaceSet: TTORIA_STUDENT_PORTAL_FINAL_UI_SURFACE_SET
    };
  }
  return null;
}

export function normalizeShellProductPresentation(raw = {}, { appId = '' } = {}) {
  const presentation = raw && typeof raw === 'object' ? raw : {};
  const surfaceAuthority = resolveFinalUiSurfaceAuthority(appId);
  const rawSurfaces = Array.isArray(presentation.enabledSurfaces)
    ? presentation.enabledSurfaces
    : [];
  const normalizedSurfaces = rawSurfaces
    .map((surfaceId) => String(surfaceId || '').trim())
    .filter(Boolean);
  const runtimeTarget = String(presentation.runtimeTarget || '').trim().slice(0, 40);
  const surfacesValid = normalizedSurfaces.length > 0
    && normalizedSurfaces.length === new Set(normalizedSurfaces).size
    && Boolean(surfaceAuthority)
    && normalizedSurfaces.every((surfaceId) => surfaceAuthority.surfaceSet.has(surfaceId));
  const uiStandard = presentation.uiStandard === TTORIA_SAAS_FINAL_UI_STANDARD
    && Boolean(surfaceAuthority)
    && (runtimeTarget === 'saas-staging' || runtimeTarget === 'saas-production')
    && surfacesValid
    ? TTORIA_SAAS_FINAL_UI_STANDARD
    : 'legacy';
  const settingsUiProfile = appId === TTORIA_SAAS_FINAL_UI_APP_ID
    && (runtimeTarget === 'saas-staging' || runtimeTarget === 'saas-production')
    && uiStandard === TTORIA_SAAS_FINAL_UI_STANDARD
    && normalizedSurfaces.includes('settings')
    && presentation.settingsUiProfile === 'reference-v2'
    ? 'reference-v2'
    : 'legacy';
  return {
    appName: String(presentation.appName || '').trim().slice(0, 120),
    shortName: String(presentation.shortName || '').trim().slice(0, 80),
    studentPortalName: String(presentation.studentPortalName || '').trim().slice(0, 120),
    wordmarkPath: String(presentation.wordmarkPath || '').trim().slice(0, 240),
    runtimeTarget,
    uiStandard,
    settingsUiProfile,
    enabledSurfaces: uiStandard === TTORIA_SAAS_FINAL_UI_STANDARD
      ? surfaceAuthority.surfaces.filter((surfaceId) => normalizedSurfaces.includes(surfaceId))
      : []
  };
}

function isShellUiStandardLocallyDisabled(storage = null) {
  try {
    const value = String(storage?.getItem?.(TTORIA_SAAS_FINAL_UI_DISABLE_STORAGE_KEY) || '')
      .trim()
      .toLowerCase();
    return value === '1' || value === 'true' || value === 'yes' || value === 'on' || value === 'disabled';
  } catch {
    return false;
  }
}

function getShellLocalStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function normalizeAuthPayload(raw = {}, {
  appId = '',
  tooltipCatalog = null,
  guideCatalog = null,
  guideRuntime = null,
} = {}) {
  const payload = raw && typeof raw === 'object' ? raw : {};
  const presentation = normalizeShellProductPresentation(payload.presentation, { appId });
  const asyncFeedbackPayload = payload.asyncFeedback && typeof payload.asyncFeedback === 'object'
    ? payload.asyncFeedback
    : {};
  const asyncFeedback = resolveTtoriaAsyncFeedbackConfig({
    standard: asyncFeedbackPayload.standard,
    appId: asyncFeedbackPayload.appId,
    target: asyncFeedbackPayload.target,
    expectedAppId: appId,
    buildId: asyncFeedbackPayload.buildId,
    artifactSha256: asyncFeedbackPayload.artifactSha256
  });
  const tooltip = normalizeTutorDeskTooltipProfile(payload.tooltip, {
    appId,
    presentation,
    catalog: tooltipCatalog,
    buildIdentity: typeof window !== 'undefined' ? window.__KG_APP_BUILD_INFO__ : null
  });
  const guide = guideCatalog && typeof guideRuntime?.normalizeProfile === 'function'
    ? guideRuntime.normalizeProfile(payload.guide, {
        appId,
        presentation,
        catalog: guideCatalog,
        buildIdentity: typeof window !== 'undefined' ? window.__KG_APP_BUILD_INFO__ : null,
      })
    : null;
  return {
    mode: String(payload.mode || payload.authMode || 'single'),
    role: String(payload.role || 'admin'),
    userEmail: String(payload.userEmail || payload.email || payload.username || ''),
    access: payload.access && typeof payload.access === 'object' ? payload.access : { list: ['*'] },
    csrfToken: String(payload.csrfToken || '').trim(),
    presentation,
    asyncFeedback,
    tooltip,
    guide,
    settingsV2: normalizeTutorDeskSettingsV2SessionProfile(payload.settingsV2),
    tenantSettingsV2: normalizeTutorDeskTenantSettingsV2RuntimeProfile(payload.tenantSettingsV2)
  };
}

function normalizeTutorDeskSettingsV2SessionProfile(raw = null) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw) || raw.scope !== 'personal') return null;
  const sections = {};
  for (const [sectionId, rawSection] of Object.entries(raw.sections || {})) {
    if (!['profile', 'appearance', 'workspace', 'assistance'].includes(sectionId)) continue;
    if (!rawSection || typeof rawSection !== 'object' || Array.isArray(rawSection)) continue;
    sections[sectionId] = {
      section: sectionId,
      data: rawSection.data && typeof rawSection.data === 'object' && !Array.isArray(rawSection.data)
        ? { ...rawSection.data }
        : {},
      revision: Math.max(0, Math.floor(Number(rawSection.revision || 0))),
      etag: String(rawSection.etag || '').trim(),
      persisted: rawSection.persisted === true,
      updatedAt: String(rawSection.updatedAt || '').trim()
    };
  }
  return {
    schemaVersion: Math.max(1, Math.floor(Number(raw.schemaVersion || 1))),
    scope: 'personal',
    activeTenantId: String(raw.activeTenantId || '').trim(),
    sections
  };
}

function normalizeTutorDeskTenantSettingsV2RuntimeProfile(raw = null) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw) || raw.scope !== 'tenant-runtime') return null;
  const sections = {};
  for (const section of ['profile', 'schedule', 'portal', 'billing', 'branding', 'ai']) {
    const value = raw[section];
    sections[section] = value && typeof value === 'object' && !Array.isArray(value) ? { ...value } : {};
  }
  return {
    schemaVersion: Math.max(1, Math.floor(Number(raw.schemaVersion || 1))),
    contractVersion: String(raw.contractVersion || '').trim(),
    scope: 'tenant-runtime',
    tenantId: String(raw.tenantId || '').trim(),
    ...sections
  };
}

export function mergeTutorDeskTenantSettingsV2RuntimeSection(currentProfile = null, section = '', rawValue = null) {
  const normalizedSection = String(section || '').trim().toLowerCase();
  if (!['profile', 'schedule', 'portal', 'billing', 'branding', 'ai'].includes(normalizedSection)) return null;
  const sectionValue = rawValue?.data && typeof rawValue.data === 'object' && !Array.isArray(rawValue.data)
    ? rawValue.data
    : rawValue;
  if (!sectionValue || typeof sectionValue !== 'object' || Array.isArray(sectionValue)) return null;
  const current = normalizeTutorDeskTenantSettingsV2RuntimeProfile(currentProfile) || {
    schemaVersion: 1,
    contractVersion: '',
    scope: 'tenant-runtime',
    tenantId: '',
    profile: {},
    schedule: {},
    portal: {},
    billing: {},
    branding: {},
    ai: {},
  };
  return normalizeTutorDeskTenantSettingsV2RuntimeProfile({
    ...current,
    [normalizedSection]: {
      ...(current[normalizedSection] || {}),
      ...sectionValue,
    },
  });
}

function readTutorDeskSettingsV2PersonalData(settingsV2 = null, section = '') {
  const data = settingsV2?.sections?.[String(section || '').trim()]?.data;
  return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
}

function resolveDevicePreference(query = '', matchMediaImpl = null) {
  try {
    return typeof matchMediaImpl === 'function' && matchMediaImpl(query)?.matches === true;
  } catch {
    return false;
  }
}

export function resolveTutorDeskSettingsV2ThemeMode(settingsV2 = null, matchMediaImpl = null) {
  if (settingsV2?.sections?.appearance?.persisted !== true) return '';
  const requested = String(readTutorDeskSettingsV2PersonalData(settingsV2, 'appearance').themeMode || '')
    .trim()
    .toLowerCase();
  if (requested === 'system') {
    return resolveDevicePreference('(prefers-color-scheme: dark)', matchMediaImpl) ? 'dark' : 'default';
  }
  if (requested === 'pretty') return 'default';
  return ['default', 'dark'].includes(requested) ? requested : '';
}

export function hasTutorDeskSettingsV2ThemePreferenceAuthority(settingsV2 = null) {
  if (settingsV2?.scope !== 'personal') return false;
  const appearance = settingsV2?.sections?.appearance;
  if (!appearance || typeof appearance !== 'object' || Array.isArray(appearance)) return false;
  const revision = Math.max(0, Math.floor(Number(appearance.revision || 0)));
  const etag = String(appearance.etag || '').trim();
  const match = etag.match(/^"td-settings-v2-personal-appearance-(\d+)"$/u);
  return Boolean(match && Number(match[1]) === revision);
}

function hasTutorDeskServerThemePrepaintAuthority(rootEl = null) {
  return String(rootEl?.getAttribute?.('data-kg-theme-preference-authority') || '').trim()
    === 'settings-v2-personal';
}

export function resolveTutorDeskSettingsV2TutorDisplayName(settingsV2 = null, authenticatedName = '') {
  const section = settingsV2?.sections?.profile;
  const savedName = section?.persisted === true
    ? readTutorDeskSettingsV2PersonalData(settingsV2, 'profile').tutorDisplayName
    : '';
  return String(savedName || authenticatedName || '')
    .replace(/\s+/gu, ' ')
    .trim()
    .slice(0, 100);
}

export function applyTutorDeskSettingsV2MotionPreference(settingsV2 = null, {
  root = typeof document !== 'undefined' ? document.documentElement : null,
  matchMediaImpl = typeof window !== 'undefined' ? window.matchMedia?.bind(window) : null
} = {}) {
  if (!root) return { mode: '', reduced: true };
  const requested = String(readTutorDeskSettingsV2PersonalData(settingsV2, 'appearance').motionMode || '')
    .trim()
    .toLowerCase();
  if (!['follow-device', 'reduce', 'allow'].includes(requested)) {
    root.removeAttribute('data-tutor-desk-motion-preference');
    return { mode: '', reduced: root.getAttribute('data-tutor-desk-motion-disabled') === 'true' };
  }
  const reduced = requested === 'reduce'
    || (requested === 'follow-device' && resolveDevicePreference('(prefers-reduced-motion: reduce)', matchMediaImpl));
  root.setAttribute('data-tutor-desk-motion-preference', requested);
  if (reduced) root.setAttribute('data-tutor-desk-motion-disabled', 'true');
  else root.removeAttribute('data-tutor-desk-motion-disabled');
  return { mode: requested, reduced };
}

function tutorDeskWorkspacePageToPluginId(value = '') {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'progress') return 'assessment';
  return ['home', 'students', 'calendar', 'payments', 'digital-library'].includes(normalized)
    ? normalized
    : '';
}

function tutorDeskPluginIdToWorkspacePage(value = '') {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'assessment') return 'progress';
  return ['home', 'students', 'calendar', 'payments', 'digital-library'].includes(normalized)
    ? normalized
    : '';
}

export function resolveTutorDeskSettingsV2OpeningPluginId(settingsV2 = null) {
  const workspace = readTutorDeskSettingsV2PersonalData(settingsV2, 'workspace');
  const requested = String(workspace.openingPage || '').trim().toLowerCase();
  return tutorDeskWorkspacePageToPluginId(
    requested === 'last-used' ? workspace.lastUsedPage : requested
  );
}

export function resolveTutorDeskSettingsV2HiddenPluginIds(settingsV2 = null) {
  const shortcuts = readTutorDeskSettingsV2PersonalData(settingsV2, 'workspace').hiddenSidebarShortcuts;
  if (!Array.isArray(shortcuts)) return new Set();
  return new Set(shortcuts.map((value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'progress' ? 'ai-quality' : normalized;
  }).filter((value) => [
    'home',
    'students',
    'students-portal',
    'assessment',
    'calendar',
    'payments',
    'digital-library',
    'ai-quality'
  ].includes(value)));
}

export function applyTutorDeskTenantBrandingProfile(tenantSettingsV2 = null, {
  root = typeof document !== 'undefined' ? document.documentElement : null,
  appNameEl = typeof document !== 'undefined' ? document.getElementById('platformAppName') : null
} = {}) {
  if (!root) return { active: false };
  const branding = tenantSettingsV2?.branding && typeof tenantSettingsV2.branding === 'object'
    ? tenantSettingsV2.branding
    : {};
  const businessName = String(tenantSettingsV2?.profile?.businessDisplayName || '').trim().slice(0, 120);
  const mode = ['subtle', 'full'].includes(String(branding.brandingMode || '').trim().toLowerCase())
    ? String(branding.brandingMode).trim().toLowerCase()
    : 'ttoria';
  const published = branding.workflow?.published && typeof branding.workflow.published === 'object'
    ? branding.workflow.published
    : {};
  const palette = published.palette && typeof published.palette === 'object' ? published.palette : {};
  const logoPath = String(published.logo?.previewPath || '').trim();
  const validLogoPath = /^\/api\/settings\/v2\/tenant\/branding\/assets\/[a-z0-9][a-z0-9_-]{7,99}\/preview$/u.test(logoPath)
    ? logoPath
    : '';
  const primaryColor = /^#[0-9A-F]{6}$/u.test(String(palette.primaryColor || '').trim())
    ? String(palette.primaryColor).trim()
    : '';
  const accentColor = /^#[0-9A-F]{6}$/u.test(String(palette.accentColor || '').trim())
    ? String(palette.accentColor).trim()
    : '';
  const active = mode !== 'ttoria' && Boolean(businessName || validLogoPath);

  root.setAttribute('data-kg-tenant-branding-mode', mode);
  root.setAttribute('data-kg-powered-by-ttoria', 'true');
  if (primaryColor) root.style?.setProperty?.('--td-tenant-brand-primary', primaryColor);
  else root.style?.removeProperty?.('--td-tenant-brand-primary');
  if (accentColor) root.style?.setProperty?.('--td-tenant-brand-accent', accentColor);
  else root.style?.removeProperty?.('--td-tenant-brand-accent');

  const doc = appNameEl?.ownerDocument;
  const brandHost = appNameEl?.parentElement;
  if (!doc || !brandHost) return { active, mode, logoPath: validLogoPath, businessName };
  let card = doc.getElementById('platformTenantBrand');
  if (!card) {
    card = doc.createElement('div');
    card.id = 'platformTenantBrand';
    card.className = 'platform-shell-layout__tenant-brand';
    const image = doc.createElement('img');
    image.className = 'platform-shell-layout__tenant-brand-logo';
    image.setAttribute('data-tenant-brand-logo', 'true');
    const copy = doc.createElement('span');
    copy.className = 'platform-shell-layout__tenant-brand-copy';
    const name = doc.createElement('span');
    name.className = 'platform-shell-layout__tenant-brand-name';
    name.setAttribute('data-tenant-brand-name', 'true');
    const attribution = doc.createElement('span');
    attribution.className = 'platform-shell-layout__tenant-brand-attribution';
    attribution.textContent = 'Powered by TToria';
    copy.append(name, attribution);
    card.append(image, copy);
    appNameEl.insertAdjacentElement('afterend', card);
  }
  const image = card.querySelector('[data-tenant-brand-logo="true"]');
  const name = card.querySelector('[data-tenant-brand-name="true"]');
  card.hidden = !active;
  if (image) {
    image.hidden = !validLogoPath;
    if (validLogoPath) {
      image.src = validLogoPath;
      image.alt = `${businessName || 'Tenant business'} logo`;
    } else {
      image.removeAttribute('src');
      image.alt = '';
    }
  }
  if (name) name.textContent = businessName || 'Your tutoring business';
  return { active, mode, logoPath: validLogoPath, businessName };
}

function disabledTutorDeskTooltipProfile(reason = 'disabled') {
  return {
    authoritative: false,
    standard: 'legacy',
    appId: TTORIA_SAAS_FINAL_UI_APP_ID,
    target: '',
    rollout: 'disabled',
    reason: String(reason || 'disabled').trim() || 'disabled'
  };
}

export function resolveTutorDeskSettingsV2TooltipPreferenceProfile(profile = {}, settingsV2 = null) {
  const appearance = readTutorDeskSettingsV2PersonalData(settingsV2, 'appearance');
  const requestedLevel = String(appearance.helpfulTooltipLevel || '').trim().toLowerCase();
  const helpfulTooltipLevel = ['off', 'essential', 'guided', 'detailed'].includes(requestedLevel)
    ? requestedLevel
    : (appearance.showHelpfulTooltips === false ? 'off' : 'essential');
  return helpfulTooltipLevel === 'off'
    ? disabledTutorDeskTooltipProfile('personal_preference_disabled')
    : { ...(profile && typeof profile === 'object' ? profile : {}), helpfulTooltipLevel };
}

export function normalizeTutorDeskTooltipProfile(raw = {}, {
  appId = '',
  presentation = {},
  catalog = null,
  buildIdentity = null
} = {}) {
  const profile = raw && typeof raw === 'object' ? raw : {};
  const runtimeCatalog = catalog && typeof catalog === 'object' ? catalog : {};
  const build = buildIdentity && typeof buildIdentity === 'object' ? buildIdentity : {};
  const tooltipBuild = build.tooltip && typeof build.tooltip === 'object' ? build.tooltip : {};
  const shaPattern = /^[a-f0-9]{64}$/u;
  const rollout = String(profile.rollout || '').trim();
  const target = String(presentation.runtimeTarget || '').trim();
  const contractSha256 = String(profile.contractSha256 || '').trim().toLowerCase();
  const inventorySha256 = String(profile.inventorySha256 || '').trim().toLowerCase();
  const catalogValid = runtimeCatalog.standard === TTORIA_TOOLTIP_STANDARD
    && runtimeCatalog.appId === TTORIA_SAAS_FINAL_UI_APP_ID
    // The shared artifact retains this alias; actual target authority is below.
    && runtimeCatalog.target === 'saas-staging'
    && Array.isArray(runtimeCatalog.eligibleTargets)
    && runtimeCatalog.eligibleTargets.length === TTORIA_TOOLTIP_ELIGIBLE_TARGETS.length
    && TTORIA_TOOLTIP_ELIGIBLE_TARGETS.every((eligibleTarget, index) => runtimeCatalog.eligibleTargets[index] === eligibleTarget)
    && runtimeCatalog.finalUiStandard === TTORIA_SAAS_FINAL_UI_STANDARD
    && Array.isArray(runtimeCatalog.actions)
    && runtimeCatalog.actions.length > 0
    && Array.isArray(runtimeCatalog.controls)
    && shaPattern.test(String(runtimeCatalog.contractSha256 || '').trim().toLowerCase())
    && shaPattern.test(String(runtimeCatalog.inventorySha256 || '').trim().toLowerCase());
  const buildValid = String(build.appId || '').trim() === TTORIA_SAAS_FINAL_UI_APP_ID
    && String(build.target || '').trim() === target
    && /^\d{14}$/u.test(String(build.buildNumber || '').trim())
    && shaPattern.test(String(build.artifactSha256 || '').trim().toLowerCase())
    && tooltipBuild.standard === TTORIA_TOOLTIP_STANDARD
    && String(tooltipBuild.contractSha256 || '').trim().toLowerCase() === contractSha256
    && String(tooltipBuild.inventorySha256 || '').trim().toLowerCase() === inventorySha256;
  if (
    profile.authoritative !== true
    || profile.standard !== TTORIA_TOOLTIP_STANDARD
    || profile.appId !== TTORIA_SAAS_FINAL_UI_APP_ID
    || profile.target !== target
    || !['canary', 'all'].includes(rollout)
    || String(appId || '').trim() !== TTORIA_SAAS_FINAL_UI_APP_ID
    || presentation?.uiStandard !== TTORIA_SAAS_FINAL_UI_STANDARD
    || !TTORIA_TOOLTIP_ELIGIBLE_TARGETS.includes(target)
    || !/^\d{14}$/u.test(String(profile.buildId || '').trim())
    || !shaPattern.test(String(profile.artifactSha256 || '').trim().toLowerCase())
    || !shaPattern.test(contractSha256)
    || !shaPattern.test(inventorySha256)
    || !catalogValid
    || !buildValid
    || String(profile.buildId || '').trim() !== String(build.buildNumber || '').trim()
    || String(profile.artifactSha256 || '').trim().toLowerCase() !== String(build.artifactSha256 || '').trim().toLowerCase()
    || contractSha256 !== String(runtimeCatalog.contractSha256 || '').trim().toLowerCase()
    || inventorySha256 !== String(runtimeCatalog.inventorySha256 || '').trim().toLowerCase()
  ) {
    return disabledTutorDeskTooltipProfile('server_profile_invalid_or_unbound');
  }
  return {
    authoritative: true,
    standard: TTORIA_TOOLTIP_STANDARD,
    appId: TTORIA_SAAS_FINAL_UI_APP_ID,
    target,
    rollout,
    buildId: String(profile.buildId || '').trim(),
    artifactSha256: String(profile.artifactSha256 || '').trim().toLowerCase(),
    contractSha256,
    inventorySha256,
    helpfulTooltipLevel: ['essential', 'guided', 'detailed'].includes(String(profile.helpfulTooltipLevel || '').trim().toLowerCase())
      ? String(profile.helpfulTooltipLevel).trim().toLowerCase()
      : 'essential'
  };
}

function applyTutorDeskTooltipStandard(config = {}, {
  root = typeof document !== 'undefined' ? document.documentElement : null
} = {}) {
  if (!root) return;
  root.removeAttribute('data-kg-tooltip-standard');
  root.removeAttribute('data-kg-tooltip-app-id');
  root.removeAttribute('data-kg-tooltip-target');
  if (config?.authoritative !== true || !TTORIA_TOOLTIP_ELIGIBLE_TARGETS.includes(config.target)) return;
  root.setAttribute('data-kg-tooltip-standard', TTORIA_TOOLTIP_STANDARD);
  root.setAttribute('data-kg-tooltip-app-id', TTORIA_SAAS_FINAL_UI_APP_ID);
  root.setAttribute('data-kg-tooltip-target', config.target);
}

function applyTtoriaAsyncFeedbackStandard(config = {}, {
  root = typeof document !== 'undefined' ? document.documentElement : null
} = {}) {
  if (!root) return;
  root.removeAttribute('data-kg-async-feedback-standard');
  root.removeAttribute('data-kg-async-feedback-app-id');
  root.removeAttribute('data-kg-async-feedback-target');
  if (config?.authoritative !== true) return;
  root.setAttribute('data-kg-async-feedback-standard', String(config.standard || 'legacy'));
  if (config.appId) root.setAttribute('data-kg-async-feedback-app-id', String(config.appId));
  if (config.target) root.setAttribute('data-kg-async-feedback-target', String(config.target));
}

function syncShellScrollIndicators(presentation = {}, {
  root = typeof document !== 'undefined' ? document.documentElement : null,
  appId = ''
} = {}) {
  const doc = root?.ownerDocument;
  const view = doc?.defaultView;
  if (!view || root !== doc.documentElement) return;
  const managerKey = Symbol.for('kg.platform-shell.scroll-indicators.v1');
  const existing = view[managerKey]?.get(doc);
  const eligible = (appId === TTORIA_SAAS_FINAL_UI_APP_ID || appId === TTORIA_STUDENT_PORTAL_FINAL_UI_APP_ID)
    && (presentation.runtimeTarget === 'saas-staging' || presentation.runtimeTarget === 'saas-production');
  // This shared runtime is also imported by standalone apps and synthetic
  // presentation fixtures. Neither should acquire document scroll behavior.
  if (!eligible || !doc.body || !doc.head
    || typeof view.HTMLElement !== 'function'
    || typeof view.ResizeObserver !== 'function'
    || typeof view.MutationObserver !== 'function'
    || typeof view.requestAnimationFrame !== 'function'
    || typeof view.cancelAnimationFrame !== 'function'
    || typeof view.getComputedStyle !== 'function'
    || typeof view.addEventListener !== 'function'
    || typeof view.removeEventListener !== 'function') {
    existing?.destroy();
    return;
  }
  if (existing) {
    existing.refresh();
    return;
  }
  // Share the registry across runtime module instances without creating it on
  // ineligible targets. The installer also owns a singleton on the root.
  const managers = view[managerKey] ||= new WeakMap();
  let controller = null;
  let suspended = false;
  const refresh = () => {
    if (suspended) return;
    controller ||= installScrollIndicators(root);
    controller.refresh();
  };
  const stop = () => {
    controller?.destroy();
    controller = null;
  };
  const destroy = () => {
    stop();
    view.removeEventListener('pagehide', onPageHide);
    view.removeEventListener('pageshow', onPageShow);
    managers.delete(doc);
  };
  const onPageHide = event => {
    suspended = true;
    stop();
    if (!event.persisted) destroy();
  };
  const onPageShow = event => {
    if (!event.persisted) return;
    suspended = false;
    refresh();
  };
  managers.set(doc, { refresh, destroy });
  view.addEventListener('pagehide', onPageHide);
  view.addEventListener('pageshow', onPageShow);
  refresh();
}

export function applyShellProductPresentation(presentation = {}, {
  root = typeof document !== 'undefined' ? document.documentElement : null,
  storage = undefined,
  appId = ''
} = {}) {
  if (!root) return;
  root.removeAttribute('data-kg-ui-standard');
  root.removeAttribute('data-kg-ui-surfaces');
  root.removeAttribute('data-kg-product-app-id');
  root.removeAttribute('data-kg-settings-ui-profile');
  if (!resolveFinalUiSurfaceAuthority(appId)) {
    syncShellScrollIndicators({}, { root, appId });
    return;
  }
  const normalized = normalizeShellProductPresentation(presentation, { appId });
  const appName = normalized.appName;
  const runtimeTarget = normalized.runtimeTarget;
  const candidateWordmarkPath = normalized.wordmarkPath;
  const wordmarkPath = /^\/assets\/[a-z0-9._/-]+$/iu.test(candidateWordmarkPath)
    ? candidateWordmarkPath
    : '/assets/tutor-desk-wordmark.png';
  root.dataset.kgProductAppName = appName || 'Tutor Desk';
  root.dataset.kgProductAppId = String(appId || '').trim();
  root.dataset.kgRuntimeTarget = runtimeTarget || 'standalone';
  root.style.setProperty('--td-shell-brand-wordmark', `url("${wordmarkPath}")`);
  if (
    normalized.uiStandard === TTORIA_SAAS_FINAL_UI_STANDARD
    && !isShellUiStandardLocallyDisabled(storage === undefined ? getShellLocalStorage() : storage)
  ) {
    root.setAttribute('data-kg-ui-standard', TTORIA_SAAS_FINAL_UI_STANDARD);
    root.setAttribute('data-kg-ui-surfaces', normalized.enabledSurfaces.join(' '));
    if (normalized.settingsUiProfile === 'reference-v2') {
      root.setAttribute('data-kg-settings-ui-profile', 'reference-v2');
    }
  }
  syncShellScrollIndicators(normalized, { root, appId });
}

function isMutatingMethod(method = 'GET') {
  const normalized = String(method || 'GET').trim().toUpperCase();
  return normalized === 'POST' || normalized === 'PUT' || normalized === 'PATCH' || normalized === 'DELETE';
}

async function fetchSessionPayload() {
  try {
    const response = await window.fetch('/auth/session', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) return null;
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    if (!contentType.includes('application/json')) return null;
    const parsed = await response.json().catch(() => null);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function buildRuntimeFetch({
  getCsrfToken = async () => '',
  defaultCredentials = 'same-origin',
  authPolicy = 'optional'
} = {}) {
  return async function runtimeFetch(url, init = {}) {
    const requestInit = { ...(init || {}) };
    const method = String(requestInit.method || 'GET').trim().toUpperCase();
    const headers = new Headers(requestInit.headers || {});

    if (isMutatingMethod(method) && !headers.has('x-csrf-token')) {
      const csrfToken = String(await getCsrfToken() || '').trim();
      if (csrfToken) {
        headers.set('x-csrf-token', csrfToken);
      }
    }

    const response = await window.fetch(url, {
      credentials: requestInit.credentials || defaultCredentials,
      ...requestInit,
      headers
    });
    if (response.status === 401) {
      const sessionExpiry = await import('./session_expiry.js');
      sessionExpiry.redirectExpiredSession(url, authPolicy);
    }
    return response;
  };
}

function buildApiClient(baseUrl = '', { runtimeFetch } = {}) {
  const normalized = String(baseUrl || '').trim();
  const hasBase = Boolean(normalized);
  const fetchImpl = typeof runtimeFetch === 'function' ? runtimeFetch : window.fetch.bind(window);

  return {
    async request(pathname = '', init = {}) {
      const pathText = String(pathname || '').trim();
      const target = hasBase
        ? new URL(pathText.replace(/^\//, ''), normalized.endsWith('/') ? normalized : normalized + '/').toString()
        : pathText;

      const response = await fetchImpl(target, {
        credentials: 'same-origin',
        ...init
      });
      const text = await response.text();
      let payload = {};
      if (text) {
        try {
          payload = JSON.parse(text);
        } catch {
          payload = {};
        }
      }

      if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'ok')) {
        if (!payload.ok) {
          const message = payload.error && payload.error.message
            ? String(payload.error.message)
            : `Request failed (${response.status})`;
          const error = new Error(message);
          error.code = response.status === 401
            ? 'AUTH_SESSION_EXPIRED'
            : payload.error && payload.error.code
              ? payload.error.code
              : 'REQUEST_FAILED';
          error.details = payload.error && payload.error.details ? payload.error.details : null;
          error.requestId = payload.requestId || '';
          throw error;
        }
        return payload.data;
      }

      if (!response.ok) {
        const error = new Error(`Request failed (${response.status})`);
        error.code = response.status === 401 ? 'AUTH_SESSION_EXPIRED' : 'REQUEST_FAILED';
        throw error;
      }

      return payload;
    }
  };
}

async function getAuthContext({
  appId = '',
  tooltipCatalog = null,
  guideCatalog = null,
  guideRuntime = null,
} = {}) {
  const payload = window.__KG_PLATFORM_AUTH__ && typeof window.__KG_PLATFORM_AUTH__ === 'object'
    ? window.__KG_PLATFORM_AUTH__
    : {};
  const normalized = normalizeAuthPayload(payload, {
    appId,
    tooltipCatalog,
    guideCatalog,
    guideRuntime,
  });
  const requiresProductPresentation = Boolean(resolveFinalUiSurfaceAuthority(appId))
    && !normalized.presentation.appName;
  const requiresAsyncFeedbackServerTruth = Boolean(TTORIA_ASYNC_FEEDBACK_TARGETS_BY_APP[appId]);
  if (!normalized.csrfToken || requiresProductPresentation || requiresAsyncFeedbackServerTruth) {
    const live = await fetchSessionPayload();
    if (live) {
      return normalizeAuthPayload({
        ...payload,
        ...live
      }, { appId, tooltipCatalog, guideCatalog, guideRuntime });
    }
  }
  if (requiresAsyncFeedbackServerTruth) {
    return {
      ...normalized,
      asyncFeedback: resolveTtoriaAsyncFeedbackConfig({ expectedAppId: appId }),
      tooltip: disabledTutorDeskTooltipProfile('server_profile_unavailable'),
      guide: guideCatalog && typeof guideRuntime?.disabledProfile === 'function'
        ? guideRuntime.disabledProfile('server_profile_unavailable')
        : null,
    };
  }
  return normalized;
}

function sortPlugins(items = []) {
  return [...items].sort((a, b) => {
    const orderDiff = a.manifest.tab.order - b.manifest.tab.order;
    if (orderDiff !== 0) return orderDiff;
    return a.manifest.id.localeCompare(b.manifest.id, undefined, { sensitivity: 'base' });
  });
}

export async function bootManifestApp({ appManifest, pluginRegistry = {}, shellRuntimeConfig = null } = {}) {
  const appValidation = validateAppManifest(appManifest);
  if (!appValidation.ok) {
    throw new Error(`Invalid app manifest: ${appValidation.errors.join(' | ')}`);
  }
  installSessionExpiryFetchInterceptor(appValidation.appManifest.authPolicy);

  // Resolve and validate the server-owned profile before theme/layout helpers
  // or any plugin renderer can measure the shell.
  const appId = String(appValidation.appManifest.appId || '').trim();
  const productPresentationEnabled = Boolean(resolveFinalUiSurfaceAuthority(appId));
  const tooltipCatalog = shellRuntimeConfig?.tooltipCatalog || null;
  const guideCatalog = shellRuntimeConfig?.guideCatalog || null;
  const guideRuntime = guideCatalog
    && typeof shellRuntimeConfig?.guideRuntime?.installBootstrap === 'function'
    && typeof shellRuntimeConfig?.guideRuntime?.styleText === 'string'
    ? shellRuntimeConfig.guideRuntime
    : null;
  const auth = await getAuthContext({ appId, tooltipCatalog, guideCatalog, guideRuntime });
  const presentedAppName = productPresentationEnabled
    ? auth.presentation?.appName
    : '';
  const effectiveAppName = String(presentedAppName || appValidation.appManifest.appName).trim()
    || appValidation.appManifest.appName;
  applyShellProductPresentation(auth.presentation, { appId });
  applyTtoriaAsyncFeedbackStandard(auth.asyncFeedback);
  applyTutorDeskTooltipStandard(auth.tooltip);
  guideRuntime?.applyStandard?.(auth.guide);
  const syncTutorDeskPersonalMotion = () => applyTutorDeskSettingsV2MotionPreference(auth.settingsV2);
  syncTutorDeskPersonalMotion();
  let personalMotionMedia = null;
  try {
    personalMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    personalMotionMedia.addEventListener?.('change', syncTutorDeskPersonalMotion);
    window.addEventListener('pagehide', () => {
      personalMotionMedia?.removeEventListener?.('change', syncTutorDeskPersonalMotion);
    }, { once: true });
  } catch {
    personalMotionMedia = null;
  }

  const fallbackTheme = getDefaultAppearanceThemeId();
  const manifestTheme = appValidation.appManifest.uiTheme || {};
  const appScope = String(manifestTheme.appScope || appValidation.appManifest.appId || '').trim()
    || appValidation.appManifest.appId;
  const manifestAppearanceId = String(manifestTheme.appearanceId || fallbackTheme.appearanceId || '').trim().toLowerCase();
  const manifestPackId = String(manifestTheme.packId || fallbackTheme.packId || '').trim().toLowerCase();
  const normalizedManifestTheme = isAppearancePack(manifestAppearanceId, manifestPackId)
    ? { appearanceId: manifestAppearanceId, packId: manifestPackId }
    : fallbackTheme;
  const persistedTheme = hasPersistedAppearanceTheme(appScope)
    ? getPersistedAppearanceTheme(appScope)
    : null;
  const effectiveTheme = persistedTheme && isAppearancePack(persistedTheme.appearanceId, persistedTheme.packId)
    ? persistedTheme
    : normalizedManifestTheme;
  applyAppearanceTheme({
    appearanceId: effectiveTheme.appearanceId,
    packId: effectiveTheme.packId,
    appScope,
    persist: false,
    emit: false
  });
  ensureDashboardTutorVisualParity({
    appId: appValidation.appManifest.appId,
    appScope,
    appearanceId: effectiveTheme.appearanceId,
    packId: effectiveTheme.packId
  });

  let authRefreshPromise = null;
  const refreshAuthContext = async () => {
    if (!authRefreshPromise) {
      authRefreshPromise = fetchSessionPayload()
        .then((payload) => {
          if (!payload) return auth;
          const normalized = normalizeAuthPayload(payload, {
            appId,
            tooltipCatalog,
            guideCatalog,
            guideRuntime,
          });
          if (normalized.csrfToken) auth.csrfToken = normalized.csrfToken;
          if (normalized.access && typeof normalized.access === 'object') auth.access = normalized.access;
          if (normalized.mode) auth.mode = normalized.mode;
          if (normalized.role) auth.role = normalized.role;
          if (normalized.userEmail) auth.userEmail = normalized.userEmail;
          auth.settingsV2 = normalized.settingsV2;
          auth.tenantSettingsV2 = normalized.tenantSettingsV2;
          applyTutorDeskTenantBrandingProfile(auth.tenantSettingsV2);
          auth.tooltip = normalized.tooltip;
          applyTutorDeskTooltipStandard(auth.tooltip);
          auth.guide = normalized.guide;
          guideRuntime?.applyStandard?.(auth.guide);
          syncTutorDeskPersonalMotion();
          if (productPresentationEnabled && normalized.presentation?.appName) {
            auth.presentation = normalized.presentation;
            applyShellProductPresentation(auth.presentation, { appId });
          } else if (productPresentationEnabled) {
            syncShellScrollIndicators(normalized.presentation, { appId });
          }
          return auth;
        })
        .catch(() => auth)
        .finally(() => {
          authRefreshPromise = null;
        });
    }
    return authRefreshPromise;
  };
  const runtimeFetch = buildRuntimeFetch({
    getCsrfToken: async () => {
      if (auth.csrfToken) return auth.csrfToken;
      await refreshAuthContext();
      return auth.csrfToken;
    },
    defaultCredentials: 'same-origin',
    authPolicy: appValidation.appManifest.authPolicy
  });
  const buildMetadata = getBuildMetadataFromWindow();
  const buildMetadataText = formatBuildMetadataLine(buildMetadata);
  const storage = safeStorage();
  const shellThemeRoot = document.documentElement || document.body;
  const prepaintedTutorDeskThemeMode = ['default', 'dark'].includes(String(
    shellThemeRoot?.getAttribute?.('data-kg-tutor-desk-theme-mode') || ''
  ).trim())
    ? String(shellThemeRoot.getAttribute('data-kg-tutor-desk-theme-mode')).trim()
    : '';
  const bridgeTutorDeskCoreThemePreference = appId === 'tutor-desk'
    && appValidation.appManifest.settingsPlugin?.appContribution?.bridgeCoreThemePreference === true
    && hasTutorDeskSettingsV2ThemePreferenceAuthority(auth.settingsV2);
  const browserLocalThemeBlocked = bridgeTutorDeskCoreThemePreference
    || hasTutorDeskServerThemePrepaintAuthority(shellThemeRoot);
  const persistedPlatformTheme = browserLocalThemeBlocked
    ? null
    : (hasPersistedPlatformThemeMode()
        ? getPersistedPlatformThemeMode()
        : null);
  const personalThemeMode = resolveTutorDeskSettingsV2ThemeMode(
    auth.settingsV2,
    typeof window !== 'undefined' ? window.matchMedia?.bind(window) : null
  );
  registerShellThemeConsumer(shellThemeRoot, {
    appliedThemeModeId: personalThemeMode || prepaintedTutorDeskThemeMode || (bridgeTutorDeskCoreThemePreference
      ? getDefaultThemeModeId()
      : (persistedPlatformTheme && persistedPlatformTheme.scopeId === 'full-app'
          ? persistedPlatformTheme.themeId
          : getDefaultThemeModeId())),
    priority: 90
  });
  setThemeCapabilityFlags({ fullAppReady: true });
  const syncTutorDeskPersonalTheme = () => {
    const themeModeId = resolveTutorDeskSettingsV2ThemeMode(
      auth.settingsV2,
      typeof window !== 'undefined' ? window.matchMedia?.bind(window) : null
    );
    const authoritativeThemeModeId = themeModeId || (bridgeTutorDeskCoreThemePreference ? getDefaultThemeModeId() : '');
    if (authoritativeThemeModeId && canApplyThemeScope('full-app')) {
      applyThemeScope('full-app', authoritativeThemeModeId, { mode: 'apply' });
    }
  };
  if (bridgeTutorDeskCoreThemePreference) {
    shellThemeRoot?.setAttribute?.('data-kg-theme-preference-authority', 'settings-v2-personal');
  }
  let personalColorSchemeMedia = null;
  try {
    personalColorSchemeMedia = window.matchMedia('(prefers-color-scheme: dark)');
    personalColorSchemeMedia.addEventListener?.('change', syncTutorDeskPersonalTheme);
    window.addEventListener('pagehide', () => {
      personalColorSchemeMedia?.removeEventListener?.('change', syncTutorDeskPersonalTheme);
    }, { once: true });
  } catch {
    personalColorSchemeMedia = null;
  }
  if (bridgeTutorDeskCoreThemePreference) syncTutorDeskPersonalTheme();

  if (String(appValidation.appManifest.shellPreset || '').trim() === 'sectioned-routes') {
    const { bootSectionedRoutesHost } = await import('./shell_host.js');
    await bootSectionedRoutesHost({
      appManifest: appValidation.appManifest,
      auth,
      buildMetadata,
      storage,
      flags: window.__KG_UI_FLAGS__ && typeof window.__KG_UI_FLAGS__ === 'object' ? window.__KG_UI_FLAGS__ : {},
      runtimeFetch,
      shellRuntimeConfig
    });
    return;
  }

  const {
    appName,
    appBuildInfo,
    navHost,
    statusHost,
    activityLaneHost,
    contextLaneHost,
    activityA11yHost,
    root,
    logoutHost
  } = ensureShellDom();
  const activityCatalog = Array.isArray(shellRuntimeConfig?.asyncFeedbackCatalog?.operations)
    ? shellRuntimeConfig.asyncFeedbackCatalog.operations
    : [];
  const logoutActivityCatalogCandidates = activityCatalog.filter((operation) => (
    operation?.lifecycleRequired !== false
    && operation?.opClass === 'user-mutation'
    && String(operation?.action || '').startsWith(`${appId}.`)
    && String(operation?.action || '').includes('.auth-logout.')
  ));
  const logoutActivityCatalogEntry = logoutActivityCatalogCandidates.find((operation) => operation?.kind === 'auth-request')
    || logoutActivityCatalogCandidates[0]
    || null;
  const activityApplicable = auth.asyncFeedback?.authoritative === true;
  const activityEnabled = auth.asyncFeedback?.enabled === true;
  const tutorDeskActivityPresentation = appId === 'tutor-desk';
  const tutorDeskSteppedActivityTargets = new Set(['create-lesson', 'save-lesson']);
  const renderActivityLaneMarkup = (model = {}) => {
    const progress = model?.phase === 'pending' ? model?.progress : null;
    const targetId = String(model?.winningActivity?.target?.id || '').trim();
    const progressValue = Number(progress?.value);
    const progressMax = Number(progress?.max);
    const progressLabel = tutorDeskActivityPresentation
      && tutorDeskSteppedActivityTargets.has(targetId)
      && Number.isInteger(progressValue)
      && Number.isInteger(progressMax)
      && progressMax > 0
      && progressValue >= 0
      && progressValue <= progressMax
      ? `Step ${progressValue} of ${progressMax}`
      : '';
    return renderTtoriaActivityLaneMarkup(model, tutorDeskActivityPresentation
      ? { showStatusBadge: false, progressLabel, indeterminateRail: true }
      : undefined);
  };
  if (activityEnabled && activityCatalog.length === 0) {
    throw new Error('TToria async feedback v1 cannot activate without its reconciled runtime operation catalog.');
  }
  const legacyStatusShell = statusHost?.closest?.('.ds-shell-status') || null;
  if (legacyStatusShell) {
    legacyStatusShell.hidden = activityEnabled;
    legacyStatusShell.setAttribute('aria-hidden', activityEnabled ? 'true' : 'false');
  }
  if (activityLaneHost) {
    activityLaneHost.hidden = !activityEnabled;
    activityLaneHost.setAttribute('aria-hidden', 'true');
    activityLaneHost.innerHTML = renderActivityLaneMarkup({ visible: false, pendingCount: 0 });
  }
  if (contextLaneHost) {
    contextLaneHost.hidden = true;
    contextLaneHost.setAttribute('aria-hidden', 'true');
    contextLaneHost.setAttribute('data-async-feedback-context-enabled', activityEnabled ? 'true' : 'false');
    contextLaneHost.innerHTML = '';
  }
  if (activityA11yHost) {
    if (activityEnabled) {
      activityA11yHost.setAttribute('role', 'status');
      activityA11yHost.setAttribute('aria-live', 'polite');
      activityA11yHost.setAttribute('aria-atomic', 'true');
    } else {
      activityA11yHost.removeAttribute('role');
      activityA11yHost.removeAttribute('aria-live');
      activityA11yHost.removeAttribute('aria-atomic');
    }
  }
  const activityLaneHome = activityLaneHost
    ? {
        parent: activityLaneHost.parentNode,
        nextSibling: activityLaneHost.nextSibling
      }
    : null;
  let activeModalActivitySlot = null;
  let activeModalActivitySurface = null;
  let activePageActivitySlot = null;
  let activeCriticalModalActivitySurface = null;
  const criticalModalDismissState = new WeakMap();

  function activityModalSurfaceFor(node = null) {
    if (!(node instanceof Element)) return null;
    const candidate = node.closest([
      '.tutor-desk-modal-surface',
      '.tutor-desk-approved-time-confirm-card',
      '[data-ds-component="ds/modal"][role="dialog"]',
      'dialog[open]'
    ].join(','));
    if (!(candidate instanceof HTMLElement)) return null;
    return candidate.closest('.tutor-desk-modal-surface') || candidate;
  }

  function isVisibleActivityModal(surface = null) {
    if (!(surface instanceof HTMLElement) || !surface.isConnected) return false;
    const bounds = surface.getBoundingClientRect();
    const style = getComputedStyle(surface);
    return bounds.width > 0
      && bounds.height > 0
      && style.display !== 'none'
      && style.visibility !== 'hidden'
      && surface.getAttribute('aria-hidden') !== 'true';
  }

  function activityDialogFor(surface = null) {
    if (!(surface instanceof HTMLElement)) return null;
    if (surface.matches('[data-ds-component="ds/modal"][role="dialog"], dialog[open]')) return surface;
    return surface.querySelector(':scope > [data-ds-component="ds/modal"][role="dialog"], :scope > dialog[open]')
      || surface.querySelector('[data-ds-component="ds/modal"][role="dialog"], dialog[open]');
  }

  function isModalDismissControl(control = null) {
    if (!(control instanceof HTMLElement)) return false;
    if (control.getAttribute('data-kg-semantic-action-id') === 'modal-chrome-close') return true;
    const label = String(control.textContent || control.getAttribute('aria-label') || '')
      .replace(/\s+/gu, ' ')
      .trim()
      .toLowerCase();
    if (label === 'close' || label === 'cancel' || label.startsWith('close ')) return true;
    const actionValues = [
      control.getAttribute('data-action'),
      control.getAttribute('data-change'),
      control.getAttribute('data-tutor-desk-status-action'),
      control.getAttribute('data-approved-time-submit-action')
    ].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);
    return actionValues.some((value) => (
      /(?:^|[-|:])(?:modal-)?(?:close|dismiss)(?:$|[-|:])/u.test(value)
      || value.endsWith('-modal-close')
    ));
  }

  function unlockCriticalModalActivity(surface = null) {
    const dialog = activityDialogFor(surface);
    if (!(dialog instanceof HTMLElement)) return;
    dialog.removeAttribute('data-platform-modal-activity-critical');
    for (const control of dialog.querySelectorAll('[data-platform-modal-activity-dismiss-locked="true"]')) {
      const state = criticalModalDismissState.get(control);
      control.removeAttribute('data-platform-modal-activity-dismiss-locked');
      if (!state) continue;
      if ('disabled' in control) control.disabled = state.disabled === true;
      if (state.ariaDisabled == null) control.removeAttribute('aria-disabled');
      else control.setAttribute('aria-disabled', state.ariaDisabled);
      if (state.uiState == null) control.removeAttribute('data-kg-ui-state');
      else control.setAttribute('data-kg-ui-state', state.uiState);
      criticalModalDismissState.delete(control);
    }
  }

  function lockCriticalModalActivity(surface = null) {
    const dialog = activityDialogFor(surface);
    if (!(dialog instanceof HTMLElement)) return false;
    dialog.setAttribute('data-platform-modal-activity-critical', 'true');
    for (const control of dialog.querySelectorAll('button, [role="button"], input[type="button"], input[type="submit"]')) {
      if (!isModalDismissControl(control)) continue;
      if (!criticalModalDismissState.has(control)) {
        criticalModalDismissState.set(control, {
          disabled: 'disabled' in control ? Boolean(control.disabled) : false,
          ariaDisabled: control.getAttribute('aria-disabled'),
          uiState: control.getAttribute('data-kg-ui-state')
        });
      }
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('data-kg-ui-state', 'disabled');
      control.setAttribute('data-platform-modal-activity-dismiss-locked', 'true');
    }
    return true;
  }

  function syncCriticalModalActivityLock(model = {}, preferredModal = null) {
    const shouldLock = tutorDeskActivityPresentation
      && model?.phase === 'pending'
      && model?.winningActivity?.opClass === 'user-mutation'
      && preferredModal instanceof HTMLElement
      && isVisibleActivityModal(preferredModal);
    if (activeCriticalModalActivitySurface && (!shouldLock || activeCriticalModalActivitySurface !== preferredModal)) {
      unlockCriticalModalActivity(activeCriticalModalActivitySurface);
      activeCriticalModalActivitySurface = null;
    }
    if (!shouldLock) return;
    if (lockCriticalModalActivity(preferredModal)) activeCriticalModalActivitySurface = preferredModal;
  }

  function visibleActivityModals() {
    const surfaces = new Set();
    for (const candidate of document.querySelectorAll([
      '.tutor-desk-modal-surface',
      '.tutor-desk-approved-time-confirm-card',
      '[data-ds-component="ds/modal"][role="dialog"]',
      'dialog[open]'
    ].join(','))) {
      const surface = activityModalSurfaceFor(candidate);
      if (isVisibleActivityModal(surface)) surfaces.add(surface);
    }
    return [...surfaces];
  }

  function activityTargetControl(activity = null) {
    const targetId = String(activity?.target?.id || '').trim();
    const actionId = String(activity?.action || '').trim();
    if (!targetId && !actionId) return null;
    for (const control of document.querySelectorAll('[data-kg-async-feedback-control]')) {
      const controlId = String(control.getAttribute('data-kg-async-feedback-control') || '').trim();
      if (controlId === targetId || controlId === actionId) return control;
    }
    return null;
  }

  function ensureModalActivitySlot(surface = null) {
    if (!(surface instanceof HTMLElement)) return null;
    const dialog = activityDialogFor(surface);
    if (!(dialog instanceof HTMLElement)) return null;
    let slot = dialog.querySelector(':scope > .ds-modal__footer > [data-platform-modal-activity-slot="true"]')
      || dialog.querySelector(':scope > [data-platform-modal-activity-slot="true"]');
    if (!(slot instanceof HTMLElement)) {
      slot = document.createElement('div');
      slot.className = 'platform-shell-modal-activity-slot';
      slot.setAttribute('data-platform-modal-activity-slot', 'true');
      slot.setAttribute('aria-hidden', 'true');
      const footer = dialog.querySelector(':scope > .ds-modal__footer');
      if (footer instanceof HTMLElement) footer.insertBefore(slot, footer.firstChild);
      else {
        const body = dialog.querySelector(':scope > .ds-modal__body');
        dialog.insertBefore(slot, body || null);
      }
    }
    return slot;
  }

  function visiblePageActivitySlot() {
    for (const slot of document.querySelectorAll('[data-platform-page-activity-slot="true"]')) {
      if (!(slot instanceof HTMLElement) || !slot.isConnected) continue;
      const bounds = slot.getBoundingClientRect();
      const style = getComputedStyle(slot);
      if (
        bounds.width > 0
        && bounds.height > 0
        && style.display !== 'none'
        && style.visibility !== 'hidden'
      ) return slot;
    }
    return null;
  }

  function clearPageActivitySlotPosition() {
    activePageActivitySlot?.removeAttribute('data-platform-page-activity-visible');
    activePageActivitySlot = null;
    if (!activityLaneHost) return;
    activityLaneHost.style.removeProperty('--platform-page-activity-left');
    activityLaneHost.style.removeProperty('--platform-page-activity-top');
    activityLaneHost.style.removeProperty('--platform-page-activity-width');
    activityLaneHost.removeAttribute('data-activity-position-source');
  }

  function positionActivityLaneAtPageSlot(slot = null) {
    if (!(slot instanceof HTMLElement) || !activityLaneHost) return false;
    const bounds = slot.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return false;
    const width = Math.min(360, bounds.width);
    const height = 50;
    const left = bounds.left + (bounds.width / 2);
    const top = bounds.top + Math.max(0, (bounds.height - height) / 2);
    clearPageActivitySlotPosition();
    activityLaneHost.style.setProperty('--platform-page-activity-left', `${left}px`);
    activityLaneHost.style.setProperty('--platform-page-activity-top', `${top}px`);
    activityLaneHost.style.setProperty('--platform-page-activity-width', `${width}px`);
    activityLaneHost.dataset.activityPositionSource = 'page-slot';
    slot.setAttribute('data-platform-page-activity-visible', 'true');
    activePageActivitySlot = slot;
    return true;
  }

  function restoreActivityLaneHome() {
    if (!activityLaneHost || !activityLaneHome) return;
    activeModalActivitySurface?.removeAttribute('data-platform-modal-activity-visible');
    clearPageActivitySlotPosition();
    if (activityLaneHost.parentNode !== activityLaneHome.parent) {
      if (activityLaneHome.nextSibling?.parentNode === activityLaneHome.parent) {
        activityLaneHome.parent.insertBefore(activityLaneHost, activityLaneHome.nextSibling);
      } else {
        activityLaneHome.parent?.appendChild?.(activityLaneHost);
      }
    }
    if (
      activeModalActivitySlot?.isConnected
      && activeModalActivitySlot.childElementCount === 0
      && activeModalActivitySlot.getAttribute('data-platform-modal-activity-static') !== 'true'
    ) {
      activeModalActivitySlot.remove();
    }
    activeModalActivitySlot = null;
    activeModalActivitySurface = null;
    activityLaneHost.dataset.activityPlacement = 'top-bar';
  }

  function syncActivityLanePlacement(model = {}) {
    if (!activityLaneHost) return;
    const activity = model?.winningActivity || null;
    const targetModal = activityModalSurfaceFor(activityTargetControl(activity));
    const currentModal = activityModalSurfaceFor(activityLaneHost);
    const fallbackModal = activity?.opClass !== 'background-refresh'
      ? visibleActivityModals().at(-1) || null
      : null;
    const preferredModal = [targetModal, currentModal, fallbackModal].find(isVisibleActivityModal) || null;
    syncCriticalModalActivityLock(model, preferredModal);
    const modal = model?.visible === true
      ? preferredModal
      : null;
    if (!modal) {
      restoreActivityLaneHome();
      if (model?.visible === true) positionActivityLaneAtPageSlot(visiblePageActivitySlot());
      return;
    }
    clearPageActivitySlotPosition();
    const slot = ensureModalActivitySlot(modal);
    if (!slot) {
      restoreActivityLaneHome();
      return;
    }
    if (activeModalActivitySurface && activeModalActivitySurface !== modal) {
      activeModalActivitySurface.removeAttribute('data-platform-modal-activity-visible');
    }
    if (activityLaneHost.parentNode !== slot) slot.appendChild(activityLaneHost);
    modal.setAttribute('data-platform-modal-activity-visible', 'true');
    slot.dataset.activityPhase = String(model?.phase || 'idle');
    activityLaneHost.dataset.activityPlacement = 'modal';
    activeModalActivitySlot = slot;
    activeModalActivitySurface = modal;
  }

  const activityPresenter = createTtoriaActivityRailController({
    enabled: activityEnabled,
    onRender(model) {
      if (!activityLaneHost) return;
      activityLaneHost.dataset.activityVisible = model.visible ? 'true' : 'false';
      activityLaneHost.dataset.activityPhase = model.phase || 'idle';
      activityLaneHost.dataset.activityTerminalState = model.terminalState || '';
      activityLaneHost.dataset.activityCount = String(model.pendingCount || 0);
      activityLaneHost.dataset.activityLongRunning = model.longRunning ? 'true' : 'false';
      activityLaneHost.setAttribute('aria-hidden', model.visible ? 'false' : 'true');
      activityLaneHost.innerHTML = renderActivityLaneMarkup(model);
      syncActivityLanePlacement(model);
    },
    onAnnouncement(message) {
      if (!activityA11yHost) return;
      activityA11yHost.textContent = message;
    }
  });
  let activityPlacementSyncQueued = false;
  const queueActivityLanePlacementSync = () => {
    if (activityPlacementSyncQueued || !activityLaneHost) return;
    activityPlacementSyncQueued = true;
    const sync = () => {
      activityPlacementSyncQueued = false;
      syncActivityLanePlacement(activityPresenter.snapshot());
    };
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(sync);
    } else {
      Promise.resolve().then(sync);
    }
  };
  const activityPlacementObserver = activityEnabled
    && typeof MutationObserver !== 'undefined'
    && document.body
    ? new MutationObserver((records = []) => {
      const model = activityPresenter.snapshot();
      const laneDetached = activityLaneHost instanceof HTMLElement && !activityLaneHost.isConnected;
      const placementChanged = laneDetached || records.some((record) => (
        [...record.addedNodes, ...record.removedNodes].some((node) => (
          node instanceof Element
          && (
            node === activityLaneHost
            || node.contains(activityLaneHost)
            || node.matches('.tutor-desk-modal-surface, .tutor-desk-approved-time-confirm-card, [data-ds-component="ds/modal"][role="dialog"], dialog[open]')
            || node.querySelector?.('.tutor-desk-modal-surface, .tutor-desk-approved-time-confirm-card, [data-ds-component="ds/modal"][role="dialog"], dialog[open]')
          )
        ))
      ));
      if (!placementChanged || (model.visible !== true && !laneDetached)) return;
      queueActivityLanePlacementSync();
    })
    : null;
  activityPlacementObserver?.observe(document.body, { childList: true, subtree: true });
  if (activityPlacementObserver && typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => activityPlacementObserver.disconnect(), { once: true });
  }
  const telemetryBatcher = createTtoriaAsyncFeedbackTelemetryBatcher({
    enabled: activityEnabled,
    flushDelayMs: 5000,
    async send(events) {
      const response = await runtimeFetch('/api/platform/v1/async-feedback/events', {
        method: 'POST', keepalive: true,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ events })
      });
      if (!response.ok) throw new Error(`Async-feedback telemetry rejected (${response.status}).`);
    }
  });
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => { void telemetryBatcher.flush(); }, { once: true });
  }
  const activityRegistry = createTtoriaActivityRegistry({
    appId,
    catalog: activityCatalog,
    onTerminal(event) {
      const activity = event.activity || {};
      if (activity.state === 'succeeded' || activity.state === 'failed') {
        activityPresenter.announceTerminal(activity);
      }
      const metrics = activityPresenter.consumeMetrics(activity.id, activity.settledAt);
      if (activity.opClass === 'legacy') return;
      telemetryBatcher.record({
        actionId: activity.action,
        operationClass: activity.opClass,
        outcome: activity.state,
        errorClass: activity.state === 'failed' ? 'unknown' : 'none',
        durationBucket: asyncFeedbackDurationBucket(event.durationMs),
        firstFeedbackBucket: asyncFeedbackFirstFeedbackBucket(metrics.firstFeedbackMs),
        railShown: metrics.railShown,
        railDwellBucket: asyncFeedbackRailDwellBucket(metrics.railDwellMs, metrics.railShown),
        duplicateSuppressed: activity.duplicateSuppressed === true,
        staleDiscarded: event.staleDiscarded === true,
        cancelRequested: activity.state === 'cancelled',
        cancelConfirmed: false,
        offlineClass: typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online',
        reconcileClass: activity.retryPolicy === 'reconcile-only' ? 'required' : 'not-required',
        retryClass: activity.retryPolicy,
        legacyLoaderViolation: false
      });
    }
  });
  const activityScopeByContext = new WeakMap();
  const activityDetachByContext = new WeakMap();
  const pluginStage = ensurePluginStage(root);
  appName.textContent = effectiveAppName;
  applyTutorDeskTenantBrandingProfile(auth.tenantSettingsV2, { appNameEl: appName });
  if (productPresentationEnabled) document.title = effectiveAppName;
  const mobileDrawer = await configureMobileDrawer({
    appManifest: {
      ...appValidation.appManifest,
      appName: effectiveAppName
    },
    appName,
    root,
    presentationMode: document.documentElement?.getAttribute('data-kg-ui-standard') === TTORIA_SAAS_FINAL_UI_STANDARD
      && String(document.documentElement?.getAttribute('data-kg-ui-surfaces') || '').split(/\s+/u).includes('shell')
      ? 'class-contract'
      : undefined
  });
  if (appBuildInfo) {
    if (buildMetadataText) {
      appBuildInfo.textContent = buildMetadataText;
      appBuildInfo.removeAttribute('hidden');
    } else {
      appBuildInfo.textContent = '';
      appBuildInfo.setAttribute('hidden', 'hidden');
    }
  }
  const capability = buildCapabilityHelpers(auth.access, true);
  let idlePrefetchTimerId = null;
  const state = {
    activeTab: '',
    activeTabName: '',
    mounted: null,
    mountedTab: '',
    pluginItems: [],
    ctx: null,
    stageEl: pluginStage.stageEl,
    hostEls: pluginStage.hostEls,
    activeHostKey: pluginStage.activeHostKey,
    activityMountSeq: 0,
    routeActivitySeq: 0,
    logoutActivitySeq: 0,
    routeEpoch: 0,
    statusSeq: 0,
    statusEntries: new Map(),
    statusTimerId: null,
    statusTimerOpKey: '',
    currentStatus: null,
    busyOverlayVisible: false,
    busyOverlayTimerId: null,
    busyOverlayHideTimerId: null,
    busyOverlayCountdownTimerId: null,
    busyOverlayShownAt: 0,
    busyOverlayMinVisibleUntil: 0,
    busyOverlayStatus: null
  };

  function getHostEl(key = '') {
    return state.hostEls[String(key || '').trim()] || null;
  }

  function getActiveHostEl() {
    return getHostEl(state.activeHostKey) || root;
  }

  function getInactiveHostKey() {
    return state.activeHostKey === 'a' ? 'b' : 'a';
  }

  function clearHost(hostEl) {
    if (!hostEl) return;
    hostEl.innerHTML = '';
  }

  function canRenderTutorDeskDestinationSkeleton(pluginId = '') {
    if (appId !== TTORIA_SAAS_FINAL_UI_APP_ID) return false;
    const documentRoot = document.documentElement;
    if (documentRoot?.getAttribute?.('data-kg-ui-standard') !== TTORIA_SAAS_FINAL_UI_STANDARD) return false;
    if (documentRoot?.getAttribute?.('data-kg-async-feedback-standard') !== 'ttoria-saas-async-feedback-v1') return false;
    if (documentRoot?.getAttribute?.('data-kg-async-feedback-app-id') !== TTORIA_SAAS_FINAL_UI_APP_ID) return false;
    const surface = normalizeTutorDeskDestinationSurface(pluginId);
    if (!surface) return false;
    const enabledSurfaces = String(documentRoot.getAttribute('data-kg-ui-surfaces') || '')
      .trim()
      .split(/\s+/u)
      .filter(Boolean);
    return enabledSurfaces.includes(surface);
  }

  function showTutorDeskDestinationSkeleton(hostEl, selectedPlugin = {}) {
    if (!hostEl || !canRenderTutorDeskDestinationSkeleton(selectedPlugin?.manifest?.id)) return false;
    const pluginId = String(selectedPlugin.manifest.id || '').trim();
    const pluginName = String(selectedPlugin.manifest.name || '').trim() || pluginId;
    const surface = normalizeTutorDeskDestinationSurface(pluginId);
    clearHost(hostEl);
    hostEl.innerHTML = renderTutorDeskDestinationSkeleton({
      surface,
      title: `Loading ${pluginName}`,
      detail: 'Preparing the selected panel.',
      testId: `tutor-desk-route-transition-${surface}-skeleton`,
      includeChrome: false,
      location: 'route-transition'
    });
    hostEl.setAttribute('data-platform-route-placeholder', surface);
    resetShellScrollPositions(hostEl);
    return true;
  }

  function clearTutorDeskDestinationSkeletonMarker(hostEl) {
    hostEl?.removeAttribute?.('data-platform-route-placeholder');
  }

  function resetShellScrollPositions(targets = []) {
    if (typeof document === 'undefined') return;
    const nodes = [
      root,
      getHostEl('a'),
      getHostEl('b'),
      document.scrollingElement,
      document.documentElement,
      document.body,
      ...(Array.isArray(targets) ? targets : [targets])
    ];
    const seen = new Set();
    for (const node of nodes) {
      if (!(node instanceof HTMLElement) || seen.has(node)) continue;
      seen.add(node);
      node.scrollTop = 0;
      node.scrollLeft = 0;
      if (typeof node.scrollTo === 'function') {
        node.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    }
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }

  function setVisibleHost(nextKey) {
    const activeKey = String(nextKey || '').trim() === 'b' ? 'b' : 'a';
    const inactiveKey = activeKey === 'a' ? 'b' : 'a';
    const activeHost = getHostEl(activeKey);
    const inactiveHost = getHostEl(inactiveKey);

    if (activeHost) {
      activeHost.classList.remove('is-inactive');
      activeHost.setAttribute('aria-hidden', 'false');
    }

    if (inactiveHost) {
      inactiveHost.classList.add('is-inactive');
      inactiveHost.setAttribute('aria-hidden', 'true');
    }

    state.activeHostKey = activeKey;
  }

  function getCurrentAccess() {
    const liveAccess = state.ctx && state.ctx.sharedState && state.ctx.sharedState.access && typeof state.ctx.sharedState.access === 'object'
      ? state.ctx.sharedState.access
      : null;
    return liveAccess || auth.access;
  }

  function setCurrentAccess(nextAccess) {
    const normalizedAccess = nextAccess && typeof nextAccess === 'object'
      ? nextAccess
      : { version: 1, list: [] };
    auth.access = normalizedAccess;
    if (window.__KG_PLATFORM_AUTH__ && typeof window.__KG_PLATFORM_AUTH__ === 'object') {
      window.__KG_PLATFORM_AUTH__.access = normalizedAccess;
    }
    if (state.ctx && state.ctx.sharedState && typeof state.ctx.sharedState === 'object') {
      state.ctx.sharedState.access = normalizedAccess;
    }
    return normalizedAccess;
  }

  function canAccess(key, fallback = true) {
    return buildCapabilityHelpers(getCurrentAccess(), true).can(key, fallback);
  }

  function getPluginTabCapabilityKey(item) {
    const manifest = item && item.manifest && typeof item.manifest === 'object' ? item.manifest : {};
    const explicitTabKey = String(
      (manifest.tab && (manifest.tab.capability || manifest.tab.capabilityKey || manifest.tab.accessKey)) || ''
    ).trim();
    if (explicitTabKey) return explicitTabKey;
    const manifestId = String(manifest.id || '').trim();
    if (!manifestId) return '';
    const tabToken = manifestId.includes('.') ? manifestId.split('.').pop() : manifestId;
    return tabToken ? `tabs.${tabToken}` : '';
  }

  function isPluginAccessible(item) {
    if (item.manifest && item.manifest.nav && item.manifest.nav.hidden) return false;
    const requiredCapabilities = Array.isArray(item.manifest.requiredCapabilities)
      ? item.manifest.requiredCapabilities
      : [];
    if (requiredCapabilities.some((key) => !canAccess(key, true))) return false;
    const tabCapabilityKey = getPluginTabCapabilityKey(item);
    if (tabCapabilityKey && !canAccess(tabCapabilityKey, true)) return false;
    return true;
  }

  function isPluginVisible(item) {
    if (!isPluginAccessible(item)) return false;
    return !resolveTutorDeskSettingsV2HiddenPluginIds(auth.settingsV2)
      .has(String(item?.manifest?.id || '').trim());
  }

  function getStatusShellEl() {
    return statusHost.closest('.ds-shell-status') || null;
  }

  function getBusyOverlayEl() {
    return document.getElementById('platformShellBusyOverlay');
  }

  function shouldUseCountdownBusyOverlay(status = {}) {
    return normalizeStatusText(status?.loaderVariant || status?.busyVariant || '') === 'students-ai-summary';
  }

  function buildBusyOverlayMarkup(status = {}) {
    const summary = escapeHtml(
      normalizeStatusText(status?.summary)
      || `Loading ${normalizeStatusText(state.activeTabName) || 'the selected tab'}`
    );
    const detail = escapeHtml(
      normalizeStatusText(status?.detail)
      || 'Working through the latest app data and view updates.'
    );
    const usesCountdown = shouldUseCountdownBusyOverlay(status);
    const countdown = usesCountdown
      ? escapeHtml(formatStatusCountdownSeconds(normalizeStatusCountdownSeconds(status?.loaderCountdownSeconds, 0) || 0))
      : '';
    const variantAttributes = usesCountdown
      ? ' data-loader-variant="students-ai-summary"'
      : '';
    const cardTestId = usesCountdown
      ? ' data-testid="students-ai-summary-floating-loader"'
      : '';
    const visualClassName = usesCountdown
      ? 'platform-shell-busy-overlay__visual platform-shell-busy-overlay__visual--countdown'
      : 'platform-shell-busy-overlay__visual';
    const finalUiAttributes = document.documentElement.getAttribute('data-kg-ui-standard') === 'ttoria-saas-final-ui-v1'
      ? ' data-kg-ui-role="blocking-loader" data-kg-ui-variant="standard" data-kg-ui-state="loading" aria-busy="true"'
      : '';
    return `
      <article id="platformShellBusyOverlay" class="platform-shell-busy-overlay" data-testid="platform-shell-busy-overlay" role="status" aria-live="polite"${finalUiAttributes}${variantAttributes}>
        <span class="platform-shell-busy-overlay__card"${cardTestId}>
          <span class="${visualClassName}" aria-hidden="true">
            <span class="platform-shell-busy-overlay__halo"></span>
            <span class="platform-shell-busy-overlay__pulse"></span>
            <span class="platform-shell-busy-overlay__core"></span>
            ${renderShellBusyOverlayIcon()}
            ${usesCountdown ? `<span class="platform-shell-busy-overlay__countdown" data-testid="students-ai-summary-loader-countdown">${countdown}</span>` : ''}
          </span>
          <span class="platform-shell-busy-overlay__copy">
            <strong class="platform-shell-busy-overlay__title">${summary}</strong>
            <span class="platform-shell-busy-overlay__detail">${detail}</span>
            <span class="platform-shell-busy-overlay__progress" aria-hidden="true">
              <span class="platform-shell-busy-overlay__progress-fill"></span>
            </span>
            <span class="platform-shell-busy-overlay__status">
              <span class="platform-shell-busy-overlay__status-icon" aria-hidden="true"></span>
              <span class="platform-shell-busy-overlay__status-text">Preparing updates...</span>
              <span class="platform-shell-busy-overlay__dots" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </span>
          </span>
        </span>
      </article>
    `;
  }

  function syncBusyOverlay(status = state.currentStatus || deriveIdleStatus()) {
    const existing = getBusyOverlayEl();
    const overlayStatus = state.busyOverlayVisible && state.busyOverlayStatus
      ? state.busyOverlayStatus
      : status;
    if (!state.busyOverlayVisible || !overlayStatus?.busy) {
      existing?.remove();
      return;
    }
    if (existing instanceof HTMLElement) {
      const nextUsesCountdown = shouldUseCountdownBusyOverlay(overlayStatus);
      const existingUsesCountdown = normalizeStatusText(existing.getAttribute('data-loader-variant') || '') === 'students-ai-summary';
      if (nextUsesCountdown !== existingUsesCountdown) {
        existing.outerHTML = buildBusyOverlayMarkup(overlayStatus);
        return;
      }
      const titleNode = existing.querySelector('.platform-shell-busy-overlay__title');
      const detailNode = existing.querySelector('.platform-shell-busy-overlay__detail');
      const countdownNode = existing.querySelector('[data-testid="students-ai-summary-loader-countdown"]');
      const summary = normalizeStatusText(overlayStatus?.summary)
        || `Loading ${normalizeStatusText(state.activeTabName) || 'the selected tab'}`;
      const detail = normalizeStatusText(overlayStatus?.detail)
        || 'Working through the latest app data and view updates.';
      if (titleNode instanceof HTMLElement && titleNode.textContent !== summary) {
        titleNode.textContent = summary;
      }
      if (detailNode instanceof HTMLElement && detailNode.textContent !== detail) {
        detailNode.textContent = detail;
      }
      if (nextUsesCountdown && countdownNode instanceof HTMLElement) {
        const countdown = formatStatusCountdownSeconds(normalizeStatusCountdownSeconds(overlayStatus?.loaderCountdownSeconds, 0) || 0);
        if (countdownNode.textContent !== countdown) countdownNode.textContent = countdown;
      }
      return;
    }
    document.body.insertAdjacentHTML('beforeend', buildBusyOverlayMarkup(overlayStatus));
  }

  function clearBusyOverlayTimer() {
    if (state.busyOverlayTimerId == null || typeof window === 'undefined') return;
    window.clearTimeout(state.busyOverlayTimerId);
    state.busyOverlayTimerId = null;
  }

  function clearBusyOverlayHideTimer() {
    if (state.busyOverlayHideTimerId == null || typeof window === 'undefined') return;
    window.clearTimeout(state.busyOverlayHideTimerId);
    state.busyOverlayHideTimerId = null;
  }

  function clearBusyOverlayCountdownTimer() {
    if (state.busyOverlayCountdownTimerId == null || typeof window === 'undefined') return;
    window.clearInterval(state.busyOverlayCountdownTimerId);
    state.busyOverlayCountdownTimerId = null;
  }

  function startBusyOverlayCountdownTimer() {
    clearBusyOverlayCountdownTimer();
    if (!state.busyOverlayVisible || !state.busyOverlayStatus?.busy) return;
    if (!shouldUseCountdownBusyOverlay(state.busyOverlayStatus)) return;
    if (typeof window === 'undefined' || typeof window.setInterval !== 'function') return;
    state.busyOverlayCountdownTimerId = window.setInterval(() => {
      if (!state.busyOverlayVisible || !state.busyOverlayStatus?.busy || !shouldUseCountdownBusyOverlay(state.busyOverlayStatus)) {
        clearBusyOverlayCountdownTimer();
        return;
      }
      const nextSeconds = Math.max(0, (normalizeStatusCountdownSeconds(state.busyOverlayStatus.loaderCountdownSeconds, 0) || 0) - 1);
      state.busyOverlayStatus = {
        ...state.busyOverlayStatus,
        loaderCountdownSeconds: nextSeconds,
      };
      syncBusyOverlay(state.busyOverlayStatus);
      if (nextSeconds <= 0) {
        clearBusyOverlayCountdownTimer();
      }
    }, 1000);
  }

  function showBusyOverlay(status = state.currentStatus || deriveIdleStatus()) {
    const normalizedStatus = status && typeof status === 'object'
      ? status
      : deriveIdleStatus();
    const now = Date.now();
    const overlayMinVisibleMs = normalizeStatusOverlayMinVisible(normalizedStatus.overlayMinVisibleMs, 0) || 0;
    clearBusyOverlayHideTimer();
    if (!state.busyOverlayVisible) {
      state.busyOverlayVisible = true;
      state.busyOverlayShownAt = now;
      state.busyOverlayMinVisibleUntil = now + overlayMinVisibleMs;
    } else if (overlayMinVisibleMs > 0) {
      state.busyOverlayMinVisibleUntil = Math.max(state.busyOverlayMinVisibleUntil, now + overlayMinVisibleMs);
    }
    state.busyOverlayStatus = {
      ...normalizedStatus,
      busy: true
    };
    syncBusyOverlay(state.busyOverlayStatus);
    startBusyOverlayCountdownTimer();
  }

  function hideBusyOverlay(status = state.currentStatus || deriveIdleStatus()) {
    clearBusyOverlayHideTimer();
    clearBusyOverlayCountdownTimer();
    state.busyOverlayVisible = false;
    state.busyOverlayShownAt = 0;
    state.busyOverlayMinVisibleUntil = 0;
    state.busyOverlayStatus = null;
    syncBusyOverlay(status);
  }

  function reconcileBusyOverlay(status = state.currentStatus || deriveIdleStatus()) {
    if (activityEnabled) {
      clearBusyOverlayTimer();
      clearBusyOverlayHideTimer();
      clearBusyOverlayCountdownTimer();
      state.busyOverlayVisible = false;
      state.busyOverlayShownAt = 0;
      state.busyOverlayMinVisibleUntil = 0;
      state.busyOverlayStatus = null;
      getBusyOverlayEl()?.remove();
      return;
    }
    const normalizedStatus = status && typeof status === 'object' ? status : deriveIdleStatus();
    if (normalizedStatus.busy) {
      if (state.busyOverlayVisible) {
        showBusyOverlay(normalizedStatus);
        return;
      }
      const overlayDelayMs = normalizeStatusOverlayDelay(normalizedStatus.overlayDelayMs, SHELL_BUSY_OVERLAY_DELAY_MS);
      if (overlayDelayMs === 0) {
        clearBusyOverlayTimer();
        showBusyOverlay(normalizedStatus);
        return;
      }
      if (state.busyOverlayTimerId != null || typeof window === 'undefined') return;
      state.busyOverlayTimerId = window.setTimeout(() => {
        state.busyOverlayTimerId = null;
        const winningStatus = getWinningStatusEntry();
        if (!winningStatus?.busy) {
          syncBusyOverlay(winningStatus);
          return;
        }
        showBusyOverlay(winningStatus);
      }, overlayDelayMs == null ? SHELL_BUSY_OVERLAY_DELAY_MS : overlayDelayMs);
      return;
    }
    clearBusyOverlayTimer();
    if (!state.busyOverlayVisible) {
      syncBusyOverlay(normalizedStatus);
      return;
    }
    const remainingVisibleMs = Math.max(0, Number(state.busyOverlayMinVisibleUntil || 0) - Date.now());
    if (remainingVisibleMs > 0 && typeof window !== 'undefined') {
      clearBusyOverlayHideTimer();
      state.busyOverlayHideTimerId = window.setTimeout(() => {
        state.busyOverlayHideTimerId = null;
        const winningStatus = getWinningStatusEntry();
        if (winningStatus?.busy) {
          reconcileBusyOverlay(winningStatus);
          return;
        }
        hideBusyOverlay(winningStatus);
      }, remainingVisibleMs);
      syncBusyOverlay();
      return;
    }
    hideBusyOverlay(normalizedStatus);
  }

  function updateStatusDebugState() {
    if (typeof window === 'undefined') return;
    window.__KG_PLATFORM_STATUS_DEBUG__ = {
      routeEpoch: state.routeEpoch,
      statusSeq: state.statusSeq,
      currentStatus: state.currentStatus ? { ...state.currentStatus } : null,
      setStatus,
      intent(detail = {}) {
        return applyStatusIntent(detail);
      },
      clear(detail = {}) {
        return clearStatusEntries(detail);
      }
    };
  }

  function clearStatusTimer() {
    if (state.statusTimerId == null || typeof window === 'undefined') return;
    window.clearTimeout(state.statusTimerId);
    state.statusTimerId = null;
    state.statusTimerOpKey = '';
  }

  function deriveIdleStatus() {
    const label = normalizeStatusText(state.activeTabName)
      || normalizeStatusText(
        state.pluginItems.find((item) => item.manifest.id === state.activeTab)?.manifest?.name
      )
      || 'shell only';
    return {
      pluginId: state.activeTab || '',
      routeKey: state.activeTab || '',
      routeEpoch: state.routeEpoch,
      opKey: `idle:${state.activeTab || 'shell'}:${state.routeEpoch}`,
      opClass: 'legacy',
      tone: 'ok',
      summary: `Ready: ${label}`,
      detail: '',
      busy: false,
      sticky: 'none',
      issuedAt: new Date().toISOString(),
      statusSeq: state.statusSeq
    };
  }

  function shouldPreserveOpenStatusControl({ nextStatus = null, appStatusHtml = '' } = {}) {
    if (
      !appStatusHtml
      || normalizeStatusOpClass(nextStatus?.opClass) === 'route-transition'
      || String(nextStatus?.routeEpoch ?? '') !== String(statusHost.dataset.statusRouteEpoch || '')
      || normalizeStatusText(nextStatus?.routeKey || '') !== normalizeStatusText(statusHost.dataset.statusRouteKey || '')
    ) {
      return false;
    }
    const currentControls = [...statusHost.querySelectorAll(
      '[data-platform-status-preserve-identity="while-open"][open]'
    )];
    const currentSignatures = [...statusHost.querySelectorAll('[data-platform-status-render-signature]')];
    if (currentControls.length !== 1 || currentSignatures.length !== 1) return false;
    const template = document.createElement('template');
    template.innerHTML = appStatusHtml;
    const nextControls = [...template.content.querySelectorAll(
      '[data-platform-status-preserve-identity="while-open"]'
    )];
    const nextSignatures = [...template.content.querySelectorAll('[data-platform-status-render-signature]')];
    if (nextSignatures.length !== 1) return false;
    const currentControlKey = normalizeStatusText(
      currentControls[0].getAttribute('data-platform-status-preserve-key')
      || currentControls[0].getAttribute('data-testid')
    );
    const matchingNextControls = nextControls.filter((control) => normalizeStatusText(
      control.getAttribute('data-platform-status-preserve-key')
      || control.getAttribute('data-testid')
    ) === currentControlKey);
    if (!currentControlKey || matchingNextControls.length !== 1) return false;
    const currentRenderSignature = normalizeStatusText(
      currentSignatures[0].getAttribute('data-platform-status-render-signature')
    );
    const nextRenderSignature = normalizeStatusText(
      nextSignatures[0].getAttribute('data-platform-status-render-signature')
    );
    return Boolean(
      currentRenderSignature
      && currentRenderSignature === nextRenderSignature
    );
  }

  function updateStatusDom(status) {
    const nextStatus = status && typeof status === 'object' ? status : deriveIdleStatus();
    const shellEl = getStatusShellEl();
    const appStatusRenderer = typeof window !== 'undefined'
      ? window.__KG_PLATFORM_STATUS_RENDERER__
      : null;
    let appStatusHtml = '';
    if (typeof appStatusRenderer === 'function') {
      try {
        appStatusHtml = String(appStatusRenderer(nextStatus) || '');
      } catch (error) {
        console.warn('[platform-shell] custom status renderer failed', error);
      }
    }
    const preserveOpenStatusControl = shouldPreserveOpenStatusControl({ nextStatus, appStatusHtml });
    if (!preserveOpenStatusControl) {
      if (appStatusHtml) {
        statusHost.innerHTML = appStatusHtml;
      } else {
        const summaryHtml = `<span class="platform-shell-layout__status-summary">${escapeHtml(nextStatus.summary || deriveIdleStatus().summary)}</span>`;
        const detailHtml = nextStatus.detail
          ? `<span class="platform-shell-layout__status-detail">${escapeHtml(nextStatus.detail)}</span>`
          : '';
        statusHost.innerHTML = [
          `<span class="platform-shell-layout__status-copy">`,
          summaryHtml,
          detailHtml,
          '</span>'
        ].join('');
      }
    }
    statusHost.dataset.statusTone = nextStatus.tone;
    statusHost.dataset.statusBusy = nextStatus.busy ? 'true' : 'false';
    statusHost.dataset.statusSticky = nextStatus.sticky;
    statusHost.dataset.statusOpClass = nextStatus.opClass;
    statusHost.dataset.statusRouteKey = nextStatus.routeKey || '';
    statusHost.dataset.statusRouteEpoch = String(nextStatus.routeEpoch);
    statusHost.dataset.statusOpKey = nextStatus.opKey;
    if (shellEl) {
      shellEl.dataset.statusTone = nextStatus.tone;
      shellEl.dataset.statusBusy = nextStatus.busy ? 'true' : 'false';
      shellEl.dataset.statusSticky = nextStatus.sticky;
      shellEl.dataset.statusOpClass = nextStatus.opClass;
      shellEl.dataset.statusRouteKey = nextStatus.routeKey || '';
      shellEl.dataset.statusRouteEpoch = String(nextStatus.routeEpoch);
      shellEl.dataset.statusOpKey = nextStatus.opKey;
    }
    state.currentStatus = nextStatus;
    reconcileBusyOverlay(nextStatus);
    updateStatusDebugState();
  }

  function getWinningStatusEntry() {
    const currentEntries = Array.from(state.statusEntries.values())
      .filter((entry) => Number(entry?.routeEpoch || 0) === Number(state.routeEpoch));
    if (!currentEntries.length) return deriveIdleStatus();
    return currentEntries.sort(compareShellStatusEntries).at(-1) || deriveIdleStatus();
  }

  function reconcileStatus() {
    clearStatusTimer();
    const winningStatus = getWinningStatusEntry();
    updateStatusDom(winningStatus);
    if (
      winningStatus
      && winningStatus.opKey
      && winningStatus.sticky === 'brief'
      && winningStatus.opKey !== deriveIdleStatus().opKey
      && typeof window !== 'undefined'
    ) {
      const timerOwner = winningStatus.opKey;
      state.statusTimerOpKey = timerOwner;
      state.statusTimerId = window.setTimeout(() => {
        const activeEntry = state.statusEntries.get(timerOwner);
        if (activeEntry && compareShellStatusEntries(activeEntry, winningStatus) === 0) {
          state.statusEntries.delete(timerOwner);
        }
        if (state.statusTimerOpKey === timerOwner) {
          state.statusTimerId = null;
          state.statusTimerOpKey = '';
        }
        reconcileStatus();
      }, BRIEF_STATUS_DECAY_MS);
    }
  }

  function resetStatusState({
    routeKey = '',
    routeLabel = '',
    incrementEpoch = false
  } = {}) {
    if (incrementEpoch) {
      state.routeEpoch += 1;
    }
    state.activeTab = normalizeStatusText(routeKey);
    state.activeTabName = normalizeStatusText(routeLabel);
    state.statusEntries.clear();
    clearStatusTimer();
    clearBusyOverlayTimer();
    clearBusyOverlayHideTimer();
    clearBusyOverlayCountdownTimer();
    state.busyOverlayVisible = false;
    state.busyOverlayShownAt = 0;
    state.busyOverlayMinVisibleUntil = 0;
    state.busyOverlayStatus = null;
    reconcileStatus();
  }

  function captureRouteState() {
    return {
      activeTab: state.activeTab,
      activeTabName: state.activeTabName,
      routeEpoch: state.routeEpoch,
      statusEntries: Array.from(state.statusEntries.entries())
    };
  }

  function restoreRouteState(snapshot = {}) {
    resetStatusState({
      routeKey: snapshot.activeTab || '',
      routeLabel: snapshot.activeTabName || '',
      incrementEpoch: false
    });
    state.routeEpoch = Number.isFinite(Number(snapshot.routeEpoch)) ? Number(snapshot.routeEpoch) : state.routeEpoch;
    state.statusEntries = new Map(Array.isArray(snapshot.statusEntries) ? snapshot.statusEntries : []);
    reconcileStatus();
  }

  function normalizeStatusPayload(detail = {}, fallback = {}) {
    const opClass = normalizeStatusOpClass(detail?.opClass, fallback.opClass || 'legacy');
    const summary = normalizeStatusText(detail?.summary || detail?.message || fallback.summary || '');
    const normalizedRouteEpoch = Number.isFinite(Number(detail?.routeEpoch))
      ? Number(detail.routeEpoch)
      : Number.isFinite(Number(fallback.routeEpoch))
        ? Number(fallback.routeEpoch)
        : state.routeEpoch;
    const pluginId = normalizeStatusText(detail?.pluginId || fallback.pluginId || state.activeTab);
    const routeKey = normalizeStatusText(detail?.routeKey || fallback.routeKey || pluginId || state.activeTab);
    const toneFallback = opClass === 'route-transition'
      ? 'warn'
      : opClass === 'legacy'
        ? 'neutral'
        : 'ok';
    const tone = normalizeStatusTone(detail?.tone, normalizeStatusTone(fallback.tone, toneFallback));
    const busy = typeof detail?.busy === 'boolean'
      ? detail.busy
      : typeof fallback.busy === 'boolean'
        ? fallback.busy
        : tone === 'warn' || opClass === 'route-transition';
    const stickyFallback = tone === 'ok'
      ? 'brief'
      : tone === 'err' || busy
        ? 'until-next-action'
        : 'none';
    const sticky = normalizeStatusSticky(detail?.sticky, normalizeStatusSticky(fallback.sticky, stickyFallback));
    const statusSeq = Number.isFinite(Number(detail?.statusSeq))
      ? Number(detail.statusSeq)
      : ++state.statusSeq;
    const fallbackAction = [
      detail?.action,
      fallback.action,
      summary,
      opClass
    ].find((value) => normalizeStatusText(value));
    const opKey = normalizeStatusText(detail?.opKey)
      || normalizeStatusText(fallback.opKey)
      || `${opClass}:${pluginId || 'shell'}:${normalizedRouteEpoch}:${normalizeStatusActionToken(fallbackAction)}:${statusSeq}`;
    return {
      pluginId,
      routeKey,
      routeEpoch: normalizedRouteEpoch,
      opKey,
      opClass,
      tone,
      summary: summary || deriveIdleStatus().summary,
      detail: normalizeStatusText(detail?.detail || fallback.detail || ''),
      busy,
      overlayDelayMs: normalizeStatusOverlayDelay(detail?.overlayDelayMs, fallback.overlayDelayMs),
      overlayMinVisibleMs: normalizeStatusOverlayMinVisible(detail?.overlayMinVisibleMs, fallback.overlayMinVisibleMs),
      loaderVariant: normalizeStatusText(detail?.loaderVariant || detail?.busyVariant || fallback.loaderVariant || fallback.busyVariant || ''),
      loaderCountdownSeconds: normalizeStatusCountdownSeconds(
        detail?.loaderCountdownSeconds ?? detail?.busyCountdownSeconds,
        fallback.loaderCountdownSeconds ?? fallback.busyCountdownSeconds
      ),
      loaderLiveOllama: typeof detail?.loaderLiveOllama === 'boolean'
        ? detail.loaderLiveOllama
        : typeof detail?.busyLiveOllama === 'boolean'
          ? detail.busyLiveOllama
          : typeof fallback.loaderLiveOllama === 'boolean'
            ? fallback.loaderLiveOllama
            : typeof fallback.busyLiveOllama === 'boolean'
              ? fallback.busyLiveOllama
              : null,
      loaderCacheHit: detail?.loaderCacheHit === true || detail?.busyCacheHit === true || fallback.loaderCacheHit === true || fallback.busyCacheHit === true,
      sticky,
      issuedAt: normalizeStatusText(detail?.issuedAt || fallback.issuedAt || new Date().toISOString()) || new Date().toISOString(),
      statusSeq
    };
  }

  function clearStatusEntries(detail = {}) {
    const normalizedOpKey = normalizeStatusText(detail?.opKey);
    const normalizedRouteEpoch = Number.isFinite(Number(detail?.routeEpoch))
      ? Number(detail.routeEpoch)
      : state.routeEpoch;
    const normalizedRouteKey = normalizeStatusText(detail?.routeKey);
    const normalizedPluginId = normalizeStatusText(detail?.pluginId);
    const normalizedOpClass = normalizeStatusOpClass(detail?.opClass, '');
    if (!normalizedOpKey && !normalizedRouteKey && !normalizedPluginId && !normalizedOpClass) {
      state.statusEntries.clear();
      reconcileStatus();
      return true;
    }
    let removed = false;
    for (const [opKey, entry] of state.statusEntries.entries()) {
      if (Number(entry?.routeEpoch || 0) !== normalizedRouteEpoch) continue;
      if (normalizedOpKey && normalizedOpKey !== opKey) continue;
      if (normalizedRouteKey && normalizedRouteKey !== normalizeStatusText(entry?.routeKey)) continue;
      if (normalizedPluginId && normalizedPluginId !== normalizeStatusText(entry?.pluginId)) continue;
      if (normalizedOpClass && normalizedOpClass !== normalizeStatusOpClass(entry?.opClass, '')) continue;
      state.statusEntries.delete(opKey);
      removed = true;
    }
    if (removed) reconcileStatus();
    return removed;
  }

  function applyStatusIntent(detail = {}) {
    const fallback = {
      pluginId: state.activeTab,
      routeKey: state.activeTab,
      routeEpoch: state.routeEpoch
    };
    const normalized = normalizeStatusPayload(detail, fallback);
    if (normalized.routeEpoch !== state.routeEpoch) {
      return null;
    }
    if (
      normalized.routeKey
      && state.activeTab
      && normalized.routeKey !== state.activeTab
      && normalized.opClass !== 'route-transition'
    ) {
      return null;
    }
    for (const [opKey, entry] of state.statusEntries.entries()) {
      if (Number(entry?.routeEpoch || 0) !== state.routeEpoch) continue;
      if (entry?.sticky !== 'none' && entry?.sticky !== 'until-next-action') continue;
      if (opKey === normalized.opKey) continue;
      if (entry?.sticky === 'until-next-action') {
        state.statusEntries.delete(opKey);
        continue;
      }
      if (entry?.sticky === 'none') {
        state.statusEntries.delete(opKey);
      }
    }
    state.statusEntries.set(normalized.opKey, normalized);
    reconcileStatus();
    return normalized;
  }

  function emitStatusIntent(detail = {}) {
    return applyStatusIntent(detail);
  }

  function emitStatusClear(detail = {}) {
    return clearStatusEntries(detail);
  }

  function setStatus(message, kind = 'warn') {
    const normalizedKind = normalizeStatusText(kind).toLowerCase();
    const legacyFallback = {
      pluginId: state.activeTab,
      routeKey: state.activeTab,
      routeEpoch: state.routeEpoch,
      action: 'legacy-status'
    };
    if (normalizedKind === 'ok') {
      emitStatusIntent({
        ...legacyFallback,
        tone: 'ok',
        busy: false,
        sticky: 'brief',
        opClass: 'legacy',
        summary: message
      });
      return;
    }
    if (normalizedKind === 'err') {
      emitStatusIntent({
        ...legacyFallback,
        tone: 'err',
        busy: false,
        sticky: 'until-next-action',
        opClass: 'legacy',
        summary: message
      });
      return;
    }
    emitStatusIntent({
      ...legacyFallback,
      tone: 'warn',
      busy: true,
      sticky: 'until-next-action',
      opClass: 'legacy',
      summary: message
    });
  }

  function clearRuntimeHealth() {
    if (typeof window === 'undefined') return;
    window.__KG_PLATFORM_RUNTIME_HEALTH__ = null;
  }

  function emitRuntimeHealth(detail = {}) {
    if (typeof window === 'undefined') return;
    const normalized = {
      status: String(detail?.status || '').trim() === 'healthy' ? 'healthy' : 'unhealthy',
      scopeType: 'selected-plugin',
      pluginId: String(detail?.pluginId || '').trim() || null,
      tabId: String(detail?.tabId || detail?.pluginId || '').trim() || null,
      message: String(detail?.message || '').trim(),
      details: detail?.details && typeof detail.details === 'object' ? detail.details : null,
      at: String(detail?.at || new Date().toISOString()).trim() || new Date().toISOString()
    };
    window.__KG_PLATFORM_RUNTIME_HEALTH__ = normalized;
    if (typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('platform:runtime-health', {
        detail: normalized
      }));
    }
  }

  function showError(message, details = '') {
    clearPluginLoadRetryNotice(getActiveHostEl());
    getActiveHostEl().innerHTML = renderDsPanel({
      label: '',
      size: 'md',
      mode: 'fixed',
      className: 'platform-shell-error-panel',
      footerHtml: '',
      bodyHtml: [
        renderDsMessage({
          component: 'ds/shell-error-message',
          className: 'platform-shell-error-panel__title',
          text: message || 'Module failed'
        }),
        details
          ? renderDsMessage({
            component: 'ds/shell-error-detail',
            className: 'platform-shell-error-panel__detail',
            text: details
          })
          : ''
      ].join('')
    });
  }

  function clearPluginLoadRetryNotice(hostEl = getActiveHostEl()) {
    if (!(hostEl instanceof HTMLElement)) return;
    hostEl.querySelectorAll('[data-platform-load-retry-notice="true"]').forEach((node) => node.remove());
  }

  async function describePluginLoadError(error) {
    const recovery = await import('./runtime_recovery.js');
    return recovery.describePluginLoadError(error);
  }

  async function showPluginLoadRetryNotice(tabId = '', pluginName = '', error = null) {
    const recovery = await import('./runtime_recovery.js');
    return recovery.showPluginLoadRetryNotice({ hostEl: getActiveHostEl(), tabId, pluginName, error });
  }

  async function loadPluginModule(entry = {}, { prefetchOnly = false } = {}) {
    if (entry && entry.module) return entry.module;
    if (!entry || typeof entry.load !== 'function') {
      throw new Error(`Plugin loader missing for ${String(entry?.manifest?.id || 'unknown').trim() || 'unknown plugin'}.`);
    }
    if (entry._loadPromise) return entry._loadPromise;
    const runner = prefetchOnly && typeof entry.prefetch === 'function'
      ? entry.prefetch
      : entry.load;
    entry._loadPromise = Promise.resolve()
      .then(() => runner())
      .then((mod) => {
        entry.module = mod;
        return entry.module;
      })
      .finally(() => {
        entry._loadPromise = null;
      });
    return entry._loadPromise;
  }

  function prefetchPluginTab(tabId = '') {
    const tab = String(tabId || '').trim();
    if (!tab) return;
    const selected = state.pluginItems.find((item) => item.manifest.id === tab);
    if (!selected || selected.module || typeof selected.load !== 'function') return;
    loadPluginModule(selected, { prefetchOnly: true }).catch(() => {});
  }

  function clearIdlePrefetch() {
    if (idlePrefetchTimerId === null) return;
    if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
      window.clearTimeout(idlePrefetchTimerId);
    }
    idlePrefetchTimerId = null;
  }

  function scheduleIdlePrefetch() {
    clearIdlePrefetch();
    const candidates = state.pluginItems.filter((item) => (
      isPluginVisible(item)
      && String(item?.manifest?.id || '').trim() !== String(state.activeTab || '').trim()
      && !item.module
      && typeof item.load === 'function'
    ));
    const nextCandidate = candidates[0];
    if (!nextCandidate) return;
    const targetTabId = String(nextCandidate.manifest.id || '').trim();
    if (!targetTabId) return;
    if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
      idlePrefetchTimerId = window.setTimeout(() => {
        idlePrefetchTimerId = null;
        prefetchPluginTab(targetTabId);
      }, 2500);
    }
  }

  function readRuntimeRouteState() {
    const switching = root.classList.contains('is-switching')
      || root.getAttribute('aria-busy') === 'true';
    const activeHost = getActiveHostEl();
    const mountedHostEntry = Object.entries(state.hostEls)
      .find(([, hostEl]) => hostEl && state.ctx?.mountEl === hostEl) || [];
    const mountedHostKey = String(mountedHostEntry[0] || '').trim();
    const mountedHost = mountedHostEntry[1] || null;
    const runtimeHealth = window.__KG_PLATFORM_RUNTIME_HEALTH__
      && typeof window.__KG_PLATFORM_RUNTIME_HEALTH__ === 'object'
      ? {
          ...window.__KG_PLATFORM_RUNTIME_HEALTH__,
          details: window.__KG_PLATFORM_RUNTIME_HEALTH__.details
            && typeof window.__KG_PLATFORM_RUNTIME_HEALTH__.details === 'object'
            ? { ...window.__KG_PLATFORM_RUNTIME_HEALTH__.details }
            : null
        }
      : null;
    const healthTab = String(runtimeHealth?.tabId || runtimeHealth?.pluginId || '').trim();
    const activeHostHidden = !activeHost
      || activeHost.classList.contains('is-inactive')
      || activeHost.getAttribute('aria-hidden') === 'true';
    const mountedHostHidden = !mountedHost
      || mountedHost.classList.contains('is-inactive')
      || mountedHost.getAttribute('aria-hidden') === 'true';
    const visibleHostKeys = Object.entries(state.hostEls)
      .filter(([, hostEl]) => (
        hostEl
        && !hostEl.classList.contains('is-inactive')
        && hostEl.getAttribute('aria-hidden') !== 'true'
      ))
      .map(([hostKey]) => String(hostKey || '').trim())
      .filter(Boolean);
    const selectedTabIds = [...navHost.querySelectorAll('[data-platform-tab][data-active="true"]')]
      .map((tabEl) => String(tabEl.getAttribute('data-platform-tab') || '').trim())
      .filter(Boolean);
    const requestedTab = String(state.activeTab || '').trim();
    const mountedTab = String(state.mountedTab || '').trim();
    return {
      requestedTab,
      mountedTab,
      routeEpoch: state.routeEpoch,
      switching,
      ready: Boolean(
        requestedTab
        && requestedTab === mountedTab
        && state.mounted
        && state.ctx
        && !switching
        && !activeHostHidden
        && mountedHost === activeHost
        && !mountedHostHidden
        && visibleHostKeys.length === 1
        && visibleHostKeys[0] === String(state.activeHostKey || '').trim()
        && selectedTabIds.length === 1
        && selectedTabIds[0] === mountedTab
        && runtimeHealth?.status === 'healthy'
        && healthTab === mountedTab
      ),
      activeHostKey: String(state.activeHostKey || '').trim(),
      activeHostHidden,
      mountedHostKey,
      mountedHostHidden,
      visibleHostKeys,
      selectedTabIds,
      runtimeHealth
    };
  }

  function publishRuntimeDebugHandle() {
    if (typeof window === 'undefined') return;
    window.__KG_PLATFORM_RUNTIME__ = {
      showTab: (tabId = '') => showTab(tabId),
      prefetchTab: (tabId = '') => prefetchPluginTab(tabId),
      getActiveTab: () => String(state.activeTab || '').trim(),
      getRouteState: () => readRuntimeRouteState(),
      getPluginStates: () => state.pluginItems.map((item) => ({
        id: String(item?.manifest?.id || '').trim(),
        name: String(item?.manifest?.name || '').trim(),
        loading: String(item?.loading || '').trim() || 'eager',
        visible: isPluginVisible(item),
        loaded: Boolean(item?.module),
        loadable: typeof item?.load === 'function'
      }))
    };
  }

  function showShellPlaceholder() {
    getActiveHostEl().innerHTML = renderDsPanel({
      label: '',
      size: 'md',
      mode: 'fixed',
      className: 'platform-shell-empty-panel',
      footerHtml: '',
      bodyHtml: [
        renderDsMessage({
          component: 'ds/shell-empty-title',
          className: 'platform-shell-empty-panel__title',
          text: 'No tabs yet'
        }),
        renderDsMessage({
          component: 'ds/shell-empty-body',
          className: 'platform-shell-empty-panel__body',
          text: 'This app is using the standard standalone shell. Add plugins to create sidebar tabs.'
        })
      ].join('')
    });
  }

  const events = new EventTarget();
  const apiClient = buildApiClient(appValidation.appManifest.apiBaseUrl, {
    runtimeFetch
  });
  let tabTransitionPromise = Promise.resolve();
  let lastUsedPageTimerId = null;

  function publishTutorDeskSettingsV2ToMountedPlugin() {
    if (state.ctx?.sharedState && typeof state.ctx.sharedState === 'object') {
      state.ctx.sharedState.settingsV2 = auth.settingsV2;
      state.ctx.sharedState.tenantSettingsV2 = auth.tenantSettingsV2;
      state.ctx.sharedState.tooltip = auth.tooltip;
      state.ctx.sharedState.guide = auth.guide;
      state.ctx.sharedState.guideRuntime = guideRuntime;
    }
  }

  async function refreshTutorDeskTooltipEnableRequest() {
    await refreshAuthContext();
    publishTutorDeskSettingsV2ToMountedPlugin();
    events.dispatchEvent(new CustomEvent('platform:settings-v2-applied', {
      detail: {
        scope: 'personal',
        section: 'appearance',
        settingsV2: auth.settingsV2,
        tooltip: auth.tooltip,
        serverRefreshed: true
      }
    }));
  }

  function readTutorDeskPersonalThemePreference() {
    const appearance = auth.settingsV2?.sections?.appearance;
    const requestedThemeModeId = String(appearance?.data?.themeMode || '').trim().toLowerCase();
    const themeModeId = requestedThemeModeId === 'pretty'
      ? 'default'
      : requestedThemeModeId;
    return Object.freeze({
      authority: bridgeTutorDeskCoreThemePreference ? 'settings-v2-personal' : '',
      themeModeId: ['default', 'dark', 'system'].includes(themeModeId) ? themeModeId : 'default',
      persisted: appearance?.persisted === true,
      revision: Number(appearance?.revision || 0),
      etag: String(appearance?.etag || '').trim(),
    });
  }

  async function persistTutorDeskPersonalThemePreference(themeModeId = '') {
    if (!bridgeTutorDeskCoreThemePreference) {
      throw Object.assign(new Error('The Tutor Desk server theme bridge is unavailable.'), { code: 'theme_preference_bridge_unavailable' });
    }
    const requestedThemeModeId = String(themeModeId || '').trim().toLowerCase();
    if (!['default', 'dark', 'system'].includes(requestedThemeModeId)) {
      throw Object.assign(new Error('Choose Light, Dark, or Follow device.'), { code: 'theme_preference_invalid' });
    }
    const patchOnce = async () => {
      let current = readTutorDeskPersonalThemePreference();
      if (!current.etag) {
        await refreshAuthContext();
        current = readTutorDeskPersonalThemePreference();
      }
      if (!current.etag) {
        throw Object.assign(new Error('A current theme preference revision is required.'), { code: 'theme_preference_revision_required' });
      }
      return runtimeFetch('/api/settings/v2/personal/appearance', {
        method: 'PATCH',
        cache: 'no-store',
        headers: {
          'content-type': 'application/json; charset=utf-8',
          'if-match': current.etag,
        },
        body: JSON.stringify({ themeMode: requestedThemeModeId }),
      });
    };
    let response = await patchOnce();
    if (response.status === 412) {
      await refreshAuthContext();
      response = await patchOnce();
    }
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const error = new Error(String(payload?.error?.message || payload?.message || 'The account theme could not be saved.'));
      error.code = String(payload?.error?.code || payload?.code || 'theme_preference_save_failed');
      error.status = response.status;
      throw error;
    }
    const payload = await response.json().catch(() => null);
    const sectionSnapshot = payload?.data && typeof payload.data === 'object' ? payload.data : null;
    if (!sectionSnapshot?.data || !String(sectionSnapshot.etag || response.headers.get('etag') || '').trim()) {
      throw Object.assign(new Error('The saved theme response did not include a strong current revision.'), { code: 'theme_preference_response_invalid' });
    }
    applyTutorDeskSettingsV2SectionChange({
      scope: 'personal',
      section: 'appearance',
      value: sectionSnapshot,
      persisted: true,
      revision: Number(sectionSnapshot.revision || 0),
      etag: String(sectionSnapshot.etag || response.headers.get('etag') || '').trim(),
    });
    return readTutorDeskPersonalThemePreference();
  }

  function applyTutorDeskSettingsV2SectionChange(detail = {}) {
    const scope = String(detail?.scope || '').trim().toLowerCase();
    const section = String(detail?.section || '').trim().toLowerCase();
    const rawValue = detail?.value && typeof detail.value === 'object'
      ? detail.value
      : (detail?.data && typeof detail.data === 'object' ? detail.data : null);
    if (!rawValue) return;
    if (['tenant', 'tenant-runtime'].includes(scope)) {
      const normalized = mergeTutorDeskTenantSettingsV2RuntimeSection(
        auth.tenantSettingsV2,
        section,
        rawValue,
      );
      if (!normalized) return;
      auth.tenantSettingsV2 = normalized;
      publishTutorDeskSettingsV2ToMountedPlugin();
      applyTutorDeskTenantBrandingProfile(auth.tenantSettingsV2);
      events.dispatchEvent(new CustomEvent('platform:settings-v2-applied', {
        detail: { scope: 'tenant', section, tenantSettingsV2: normalized }
      }));
      return;
    }
    if (scope !== 'personal') return;
    if (!['profile', 'appearance', 'workspace', 'assistance'].includes(section)) return;
    const current = auth.settingsV2 || {
      schemaVersion: 1,
      scope: 'personal',
      activeTenantId: '',
      sections: {}
    };
    const normalized = normalizeTutorDeskSettingsV2SessionProfile({
      ...current,
      sections: {
        ...(current.sections || {}),
        [section]: {
          section,
          ...(rawValue || {}),
          persisted: detail.persisted === true || rawValue.persisted === true,
          revision: Number.isInteger(detail.revision) ? detail.revision : rawValue.revision,
          etag: String(detail.etag || rawValue.etag || '').trim(),
          data: rawValue.data && typeof rawValue.data === 'object'
            ? rawValue.data
            : rawValue
        }
      }
    });
    if (!normalized) return;
    if (section === 'workspace') {
      const currentWorkspace = current.sections?.workspace;
      const incomingWorkspace = normalized.sections?.workspace;
      const incomingRevision = Number.isInteger(detail.revision) ? detail.revision : rawValue.revision;
      // A successful navigation response can arrive after a newer Settings save.
      // Keep the newer server revision before publishing to mounted consumers.
      if (
        currentWorkspace?.persisted === true
        && incomingWorkspace?.persisted === true
        && Number.isSafeInteger(currentWorkspace.revision)
        && Number.isSafeInteger(incomingRevision)
        && incomingWorkspace.revision > 0
        && incomingWorkspace.revision < currentWorkspace.revision
        && currentWorkspace.etag === `"td-settings-v2-personal-workspace-${currentWorkspace.revision}"`
        && incomingWorkspace.etag === `"td-settings-v2-personal-workspace-${incomingWorkspace.revision}"`
      ) return;
    }
    auth.settingsV2 = normalized;
    if (section === 'appearance') {
      const showHelpfulTooltips = readTutorDeskSettingsV2PersonalData(normalized, 'appearance').showHelpfulTooltips;
      auth.tooltip = resolveTutorDeskSettingsV2TooltipPreferenceProfile(auth.tooltip, normalized);
      applyTutorDeskTooltipStandard(auth.tooltip);
    }
    publishTutorDeskSettingsV2ToMountedPlugin();
    syncTutorDeskPersonalMotion();
    syncTutorDeskPersonalTheme();
    renderNavButtons(state.activeTab);
    events.dispatchEvent(new CustomEvent('platform:settings-v2-applied', {
      detail: { scope: 'personal', section, settingsV2: normalized }
    }));
    if (
      section === 'appearance'
      && readTutorDeskSettingsV2PersonalData(normalized, 'appearance').showHelpfulTooltips === true
    ) {
      void refreshTutorDeskTooltipEnableRequest();
    }
  }

  async function persistTutorDeskLastUsedPage(pluginId = '') {
    const workspacePage = tutorDeskPluginIdToWorkspacePage(pluginId);
    const workspaceSection = auth.settingsV2?.sections?.workspace;
    if (!workspacePage || !workspaceSection?.etag || workspaceSection.data?.lastUsedPage === workspacePage) return;
    const optimisticSection = {
      ...workspaceSection,
      data: { ...(workspaceSection.data || {}), lastUsedPage: workspacePage }
    };
    auth.settingsV2 = normalizeTutorDeskSettingsV2SessionProfile({
      ...auth.settingsV2,
      sections: { ...auth.settingsV2.sections, workspace: optimisticSection }
    });
    publishTutorDeskSettingsV2ToMountedPlugin();
    try {
      const response = await runtimeFetch('/api/settings/v2/personal/workspace', {
        method: 'PATCH',
        cache: 'no-store',
        headers: {
          'content-type': 'application/json; charset=utf-8',
          'if-match': workspaceSection.etag
        },
        body: JSON.stringify({ lastUsedPage: workspacePage })
      });
      if (!response.ok) {
        if (response.status === 412) await refreshAuthContext();
        return;
      }
      const payload = await response.json().catch(() => null);
      const nextSection = payload?.data && typeof payload.data === 'object' ? payload.data : null;
      if (nextSection) {
        applyTutorDeskSettingsV2SectionChange({ scope: 'personal', section: 'workspace', value: nextSection });
      }
    } catch (error) {
      console.warn('[platform-shell] last used page preference was not persisted', error);
    }
  }

  function scheduleTutorDeskLastUsedPage(pluginId = '') {
    if (lastUsedPageTimerId != null) window.clearTimeout(lastUsedPageTimerId);
    lastUsedPageTimerId = window.setTimeout(() => {
      lastUsedPageTimerId = null;
      void persistTutorDeskLastUsedPage(pluginId);
    }, 400);
  }

  events.addEventListener('platform:settings-v2-changed', (event) => {
    applyTutorDeskSettingsV2SectionChange(event?.detail || {});
  });

  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    const onTutorDeskThemePreferenceRequest = async (event) => {
      if (!bridgeTutorDeskCoreThemePreference) return;
      const requestedThemeModeId = String(event?.detail?.themeModeId || '').trim().toLowerCase();
      try {
        const saved = await persistTutorDeskPersonalThemePreference(requestedThemeModeId);
        window.dispatchEvent(new CustomEvent('platform:tutor-desk-theme-preference-result', {
          detail: { ok: true, ...saved },
        }));
      } catch (error) {
        await refreshAuthContext();
        syncTutorDeskPersonalTheme();
        window.dispatchEvent(new CustomEvent('platform:tutor-desk-theme-preference-result', {
          detail: {
            ok: false,
            themeModeId: readTutorDeskPersonalThemePreference().themeModeId,
            code: String(error?.code || 'theme_preference_save_failed'),
            message: String(error?.message || 'The account theme could not be saved.'),
          },
        }));
      }
    };
    if (bridgeTutorDeskCoreThemePreference) {
      window.addEventListener('platform:tutor-desk-theme-preference-request', onTutorDeskThemePreferenceRequest);
    }
    window.addEventListener('platform:status-intent', (event) => {
      emitStatusIntent(event?.detail || {});
    });
    window.addEventListener('platform:status-clear', (event) => {
      emitStatusClear(event?.detail || {});
    });
    window.addEventListener('pagehide', () => {
      if (bridgeTutorDeskCoreThemePreference) {
        window.removeEventListener('platform:tutor-desk-theme-preference-request', onTutorDeskThemePreferenceRequest);
      }
      if (lastUsedPageTimerId != null) window.clearTimeout(lastUsedPageTimerId);
      lastUsedPageTimerId = null;
    }, { once: true });
  }

  reconcileStatus();

  async function unmountLifecycle(lifecycle, ctx) {
    const activityScope = ctx && typeof ctx === 'object' ? activityScopeByContext.get(ctx) : null;
    const detachActivity = ctx && typeof ctx === 'object' ? activityDetachByContext.get(ctx) : null;
    if (typeof detachActivity === 'function') detachActivity();
    if (activityScope && typeof activityScope.invalidate === 'function') activityScope.invalidate();
    if (ctx && typeof ctx === 'object') {
      activityScopeByContext.delete(ctx);
      activityDetachByContext.delete(ctx);
    }
    if (!lifecycle || typeof lifecycle.unmount !== 'function') return;
    try {
      await lifecycle.unmount(ctx);
    } catch (error) {
      console.warn('[platform-shell] unmount failed', error);
    }
  }

  async function unmountCurrent() {
    await unmountLifecycle(state.mounted, state.ctx);
    clearHost(getActiveHostEl());
    clearRuntimeHealth();
    state.mounted = null;
    state.mountedTab = '';
    state.ctx = null;
  }

  function beginTutorDeskPanelRouteActivity(selectedPlugin = {}) {
    if (!activityEnabled || appId !== TTORIA_SAAS_FINAL_UI_APP_ID) return null;
    const pluginId = String(selectedPlugin?.manifest?.id || '').trim();
    if (!pluginId || !canRenderTutorDeskDestinationSkeleton(pluginId)) return null;
    const pluginName = String(selectedPlugin?.manifest?.name || '').trim() || pluginId;
    const activityMountId = `route-${++state.routeActivitySeq}-${pluginId}`;
    const presenterKey = `platform-shell-route:${state.routeEpoch}:${activityMountId}`;
    const scope = activityRegistry.createScope({
      pluginId: 'platform-shell',
      routeKey: pluginId,
      routeEpoch: state.routeEpoch,
      mountId: activityMountId
    });
    const detachPresenter = activityPresenter.attach(scope, presenterKey);
    let settled = false;
    let handle = null;
    const cleanup = () => {
      detachPresenter();
      scope.invalidate();
    };
    try {
      handle = scope.begin({
        action: 'navigate-panel',
        opClass: 'route-transition',
        summary: `Loading ${pluginName}`,
        detail: 'Preparing the selected panel.'
      });
    } catch (error) {
      cleanup();
      console.warn('[ttoria-activity] panel route activity failed to begin', error);
      return null;
    }
    return Object.freeze({
      succeed() {
        if (settled) return false;
        settled = true;
        const result = handle.succeed({
          summary: `${pluginName} is ready`,
          detail: 'All systems operational.'
        });
        cleanup();
        return result;
      },
      fail(detail = '') {
        if (settled) return false;
        settled = true;
        const result = handle.fail({
          summary: `${pluginName} failed to load`,
          detail: String(detail || '').trim()
        });
        cleanup();
        return result;
      }
    });
  }

  function beginPlatformShellLogoutActivity() {
    if (!activityEnabled || !logoutActivityCatalogEntry?.action) return null;
    const activityMountId = `logout-${++state.logoutActivitySeq}`;
    const presenterKey = `platform-shell-logout:${state.routeEpoch}:${activityMountId}`;
    const scope = activityRegistry.createScope({
      pluginId: 'platform-shell',
      routeKey: state.activeTab || appId,
      routeEpoch: state.routeEpoch,
      mountId: activityMountId
    });
    const detachPresenter = activityPresenter.attach(scope, presenterKey);
    let settled = false;
    let handle = null;
    const cleanup = () => {
      detachPresenter();
      scope.invalidate();
    };
    try {
      handle = scope.begin({
        action: logoutActivityCatalogEntry.action,
        opClass: 'user-mutation',
        summary: 'Signing out securely',
        detail: 'Ending your current session.'
      });
    } catch (error) {
      cleanup();
      console.warn('[ttoria-activity] logout activity failed to begin', error);
      return null;
    }
    return Object.freeze({
      succeed() {
        if (settled) return false;
        settled = true;
        const result = handle.succeed({
          summary: 'Signed out',
          detail: 'Redirecting to sign in.'
        });
        cleanup();
        return result;
      },
      fail(detail = '') {
        if (settled) return false;
        settled = true;
        const result = handle.fail({
          summary: 'Sign out could not be confirmed',
          detail: String(detail || '').trim() || 'Returning to sign in.'
        });
        cleanup();
        return result;
      }
    });
  }

  function showTab(tabId) {
    const transition = tabTransitionPromise
      .catch(() => {})
      .then(() => showTabNow(tabId));
    tabTransitionPromise = transition.catch(() => {});
    return transition;
  }

  async function showTabNow(tabId) {
    clearIdlePrefetch();
    const tab = String(tabId || '').trim();
    const selected = state.pluginItems.find((item) => item.manifest.id === tab);
    if (!selected) {
      showError('Unknown plugin tab', tab);
      setStatus('Unknown tab requested', 'err');
      return;
    }
    const allowSameTabReactivate = appValidation.appManifest.appId === 'tutor-desk' && selected.manifest.id === 'home';
    const mountedHostVisible = state.ctx?.mountEl instanceof HTMLElement && state.ctx.mountEl.offsetParent !== null;
    const mountedCtxMatches = String(state.ctx?.pluginId || '').trim() === tab;
    const expectedRootTestId = /^[a-z0-9-]+$/iu.test(tab) ? `${tab}-plugin-root` : '';
    const mountedPluginRootVisible = expectedRootTestId
      ? Boolean(Array.from(state.ctx?.mountEl?.querySelectorAll?.(`[data-testid="${expectedRootTestId}"]`) || [])
        .some((element) => element instanceof HTMLElement && element.offsetParent !== null))
      : mountedHostVisible;
    if (
      tab
      && tab === state.activeTab
      && tab === state.mountedTab
      && state.mounted
      && state.ctx
      && mountedHostVisible
      && mountedCtxMatches
      && mountedPluginRootVisible
    ) {
      state.activeTab = selected.manifest.id;
      state.activeTabName = normalizeStatusText(selected.manifest.name) || selected.manifest.id;
      renderNavButtons(tab);
      clearRuntimeHealth();
      if (allowSameTabReactivate && typeof state.mounted.reactivate === 'function') {
        emitStatusIntent({
          pluginId: selected.manifest.id,
          routeKey: selected.manifest.id,
          routeEpoch: state.routeEpoch,
          opKey: `route-transition:${selected.manifest.id}:${state.routeEpoch}`,
          opClass: 'route-transition',
          tone: 'warn',
          summary: `Resetting ${selected.manifest.name}`,
          detail: 'Restoring the default Home view.',
          busy: true,
          sticky: 'until-next-action'
        });
        try {
          await state.mounted.reactivate(state.ctx);
        } catch (error) {
          console.error('[platform-shell] plugin reset failed', error);
          emitStatusIntent({
            pluginId: selected.manifest.id,
            routeKey: selected.manifest.id,
            routeEpoch: state.routeEpoch,
            opKey: `route-transition:${selected.manifest.id}:${state.routeEpoch}`,
            opClass: 'route-transition',
            tone: 'err',
            summary: `${selected.manifest.name} failed to reset`,
            detail: error && error.message ? error.message : String(error),
            busy: false,
            sticky: 'until-next-action'
          });
          emitRuntimeHealth({
            status: 'unhealthy',
            pluginId: selected.manifest.id,
            tabId: selected.manifest.id,
            message: 'Plugin reactivation failed',
            details: {
              pluginName: String(selected.manifest.name || '').trim() || selected.manifest.id
            }
          });
          showError(`${selected.manifest.name} failed to reset.`, error && error.message ? error.message : String(error));
          return;
        }
      }
      emitStatusIntent({
        pluginId: selected.manifest.id,
        routeKey: selected.manifest.id,
        routeEpoch: state.routeEpoch,
        opKey: `route-transition:${selected.manifest.id}:${state.routeEpoch}`,
        opClass: 'route-transition',
        tone: 'ok',
        summary: `Ready: ${selected.manifest.name}`,
        busy: false,
        sticky: 'brief'
      });
      emitRuntimeHealth({
        status: 'healthy',
        pluginId: selected.manifest.id,
        tabId: selected.manifest.id,
        message: `Ready: ${selected.manifest.name}`,
        details: {
          pluginName: String(selected.manifest.name || '').trim() || selected.manifest.id
        }
      });
      return;
    }

    const requiredCapabilities = Array.isArray(selected.manifest.requiredCapabilities)
      ? selected.manifest.requiredCapabilities
      : [];
    const blockedCap = requiredCapabilities.find((key) => !canAccess(key, true));
    if (blockedCap) {
      showError('Access denied for plugin', blockedCap);
      setStatus('Capability denied', 'err');
      return;
    }
    const blockedTabCap = getPluginTabCapabilityKey(selected);
    if (blockedTabCap && !canAccess(blockedTabCap, true)) {
      showError('Access denied for plugin', blockedTabCap);
      setStatus('Capability denied', 'err');
      return;
    }

    const previousRouteState = captureRouteState();
    const incrementEpoch = selected.manifest.id !== state.activeTab;
    resetStatusState({
      routeKey: selected.manifest.id,
      routeLabel: selected.manifest.name,
      incrementEpoch
    });
    const panelRouteActivity = beginTutorDeskPanelRouteActivity(selected);
    const routeStatusKey = `route-transition:${selected.manifest.id}:${state.routeEpoch}`;
    emitStatusIntent({
      pluginId: selected.manifest.id,
      routeKey: selected.manifest.id,
      routeEpoch: state.routeEpoch,
      opKey: routeStatusKey,
      opClass: 'route-transition',
      tone: 'warn',
      summary: `Loading ${selected.manifest.name}`,
      detail: 'Preparing the selected tab.',
      busy: true,
      sticky: 'until-next-action'
    });
    renderNavButtons(selected.manifest.id);
    clearRuntimeHealth();
    root.classList.add('is-switching');
    root.setAttribute('aria-busy', 'true');
    const previousLifecycle = state.mounted;
    const previousCtx = state.ctx;
    const previousHostKey = state.activeHostKey;
    const previousHost = getHostEl(previousHostKey);
    const nextHostKey = getInactiveHostKey();
    const nextMountEl = getHostEl(nextHostKey) || root;
    const destinationSkeletonVisible = showTutorDeskDestinationSkeleton(nextMountEl, selected);
    if (destinationSkeletonVisible) setVisibleHost(nextHostKey);
    await waitForPaint(1);
    let loadedModule = selected.module;
    let nextLifecycle = null;
    let nextCtx = null;
    try {
      loadedModule = await loadPluginModule(selected);
    } catch (error) {
      if (destinationSkeletonVisible && previousHost) setVisibleHost(previousHostKey);
      clearTutorDeskDestinationSkeletonMarker(nextMountEl);
      clearHost(nextMountEl);
      root.classList.remove('is-switching');
      root.setAttribute('aria-busy', 'false');
      restoreRouteState(previousRouteState);
      renderNavButtons(state.activeTab);
      const recovery = await describePluginLoadError(error);
      const errorCode = recovery.code;
      const safeError = recovery.message;
      panelRouteActivity?.fail(safeError);
      emitRuntimeHealth({
        status: 'unhealthy',
        pluginId: selected.manifest.id,
        tabId: selected.manifest.id,
        message: 'Plugin load failed',
        details: {
          pluginName: String(selected.manifest.name || '').trim() || selected.manifest.id,
          errorCode,
          error: safeError
        }
      });
      if (previousLifecycle && previousHost) {
        await showPluginLoadRetryNotice(selected.manifest.id, selected.manifest.name, error);
        return;
      }
      console.error('[platform-shell] plugin load failed', error);
      showError('Plugin failed to load.', safeError);
      return;
    }
    try {
      resetShellScrollPositions([previousHost, nextMountEl]);
      if (previousLifecycle && previousHost && previousHost === nextMountEl) {
        await unmountLifecycle(previousLifecycle, previousCtx);
      }

      const lifecycle = resolvePluginLifecycle(loadedModule);
      nextLifecycle = lifecycle;
      const liveAccess = getCurrentAccess();
      const pluginManifests = state.pluginItems.map((item) => {
        const manifest = item && item.manifest && typeof item.manifest === 'object'
          ? item.manifest
          : {};
        return {
          ...manifest,
          requiredCapabilities: Array.isArray(manifest.requiredCapabilities)
            ? [...manifest.requiredCapabilities]
            : [],
          publishedCapabilities: Array.isArray(manifest.publishedCapabilities)
            ? [...manifest.publishedCapabilities]
            : [],
          featureFlags: Array.isArray(manifest.featureFlags)
            ? [...manifest.featureFlags]
            : [],
          tab: manifest.tab && typeof manifest.tab === 'object' ? { ...manifest.tab } : manifest.tab,
          nav: manifest.nav && typeof manifest.nav === 'object' ? { ...manifest.nav } : manifest.nav
        };
      });

      const sharedState = {
        csrfToken: auth.csrfToken,
        access: liveAccess,
        authMode: auth.mode,
        userRole: auth.role,
        userEmail: auth.userEmail,
        appId: appValidation.appManifest.appId,
        appName: effectiveAppName,
        buildId: String(buildMetadata?.buildNumber || '').trim(),
        shellPreset: appValidation.appManifest.shellPreset,
        settingsPlugin: appValidation.appManifest.settingsPlugin,
        pluginManifests,
        asyncFeedback: auth.asyncFeedback,
        tooltip: auth.tooltip,
        tooltipCatalog,
        guide: auth.guide,
        guideCatalog,
        guideRuntime,
        settingsV2: auth.settingsV2,
        tenantSettingsV2: auth.tenantSettingsV2,
        themePreference: bridgeTutorDeskCoreThemePreference
          ? Object.freeze({
              authority: 'settings-v2-personal',
              read: readTutorDeskPersonalThemePreference,
              save: persistTutorDeskPersonalThemePreference,
            })
          : null
      };
      const capabilities = {
        can: (key, fallback = true) => canAccess(key, fallback)
      };
      Object.defineProperty(capabilities, 'access', {
        enumerable: true,
        get() {
          return sharedState.access;
        }
      });
      const plugins = createDeclaredPluginAccess({
        getPluginItems: () => state.pluginItems,
        loadPluginModule
      });

      const activityMountId = `mount-${++state.activityMountSeq}-${selected.manifest.id}`;
      const activityScope = activityRegistry.createScope({
        pluginId: selected.manifest.id,
        routeKey: selected.manifest.id,
        routeEpoch: state.routeEpoch,
        mountId: activityMountId
      });
      const detachActivityPresenter = activityPresenter.attach(
        activityScope,
        `${selected.manifest.id}:${state.routeEpoch}:${activityMountId}`
      );
      const ctx = {
        mountEl: nextMountEl,
        root: nextMountEl,
        pluginId: selected.manifest.id,
        pluginName: String(selected.manifest.name || '').trim() || selected.manifest.id,
        routeKey: selected.manifest.id,
        routeEpoch: state.routeEpoch,
        apiClient,
        auth: {
          mode: auth.mode,
          role: auth.role,
          userEmail: auth.userEmail
        },
        capabilities,
        plugins,
        fetch: runtimeFetch,
        credentials: 'same-origin',
        sharedState,
        ui: {
          setStatus,
          activity: Object.freeze({
            begin(detail = {}) {
              return activityScope.begin(detail);
            },
            snapshot(selector) {
              return activityScope.snapshot(selector);
            },
            subscribe(selector, listener) {
              return activityScope.subscribe(selector, listener);
            }
          }),
          status: {
            intent(detail = {}) {
              const normalizedDetail = {
                pluginId: selected.manifest.id,
                routeKey: selected.manifest.id,
                routeEpoch: state.routeEpoch,
                ...detail
              };
              const result = emitStatusIntent(normalizedDetail);
              if (activityApplicable) {
                try {
                  activityScope.legacyAdapter.intent({
                    ...detail,
                    pluginId: selected.manifest.id,
                    routeKey: selected.manifest.id,
                    routeEpoch: state.routeEpoch
                  });
                } catch {
                  console.warn('[ttoria-activity] legacy status adapter failed');
                }
              }
              return result;
            },
            clear(detail = {}) {
              const normalizedDetail = {
                pluginId: selected.manifest.id,
                routeKey: selected.manifest.id,
                routeEpoch: state.routeEpoch,
                ...detail
              };
              const result = emitStatusClear(normalizedDetail);
              if (activityApplicable) {
                try {
                  activityScope.legacyAdapter.clear({
                    ...detail,
                    pluginId: selected.manifest.id,
                    routeKey: selected.manifest.id,
                    routeEpoch: state.routeEpoch
                  });
                } catch {
                  console.warn('[ttoria-activity] legacy status adapter failed');
                }
              }
              return result;
            }
          },
          showError,
          showTab,
          renderNavButtons,
          setAccess(nextAccess) {
            setCurrentAccess(nextAccess);
            const normalizedAccess = getCurrentAccess();
            if (state.ctx && state.ctx.sharedState && typeof state.ctx.sharedState === 'object') {
              state.ctx.sharedState.access = normalizedAccess;
            }
            events.dispatchEvent(new CustomEvent('platform:access-changed', {
              detail: { access: normalizedAccess }
            }));
            renderNavButtons(state.activeTab);
          }
        },
        events,
        logger: (...args) => console.log(`[plugin:${selected.manifest.id}]`, ...args),
        storage,
        flags: window.__KG_UI_FLAGS__ && typeof window.__KG_UI_FLAGS__ === 'object' ? window.__KG_UI_FLAGS__ : {}
      };
      nextCtx = ctx;
      activityScopeByContext.set(ctx, activityScope);
      activityDetachByContext.set(ctx, detachActivityPresenter);

      await lifecycle.mount(ctx);
      clearTutorDeskDestinationSkeletonMarker(nextMountEl);
      clearPluginLoadRetryNotice(previousHost);
      reconcileSettingsBuildInfoCard(nextMountEl, {
        appName: effectiveAppName,
        buildMetadataText
      });

      const livePersonalThemeMode = resolveTutorDeskSettingsV2ThemeMode(
        auth.settingsV2,
        typeof window !== 'undefined' ? window.matchMedia?.bind(window) : null
      );
      const livePlatformTheme = livePersonalThemeMode
        ? { scopeId: 'full-app', themeId: livePersonalThemeMode }
        : (bridgeTutorDeskCoreThemePreference
            ? { scopeId: 'full-app', themeId: getDefaultThemeModeId() }
            : (hasPersistedPlatformThemeMode() ? getPersistedPlatformThemeMode() : null));
      if (livePlatformTheme && canApplyThemeScope(livePlatformTheme.scopeId)) {
        applyThemeScope(livePlatformTheme.scopeId, livePlatformTheme.themeId, { mode: 'apply' });
      }

      await waitForPaint(2);
      setVisibleHost(nextHostKey);
      resetShellScrollPositions(getHostEl(nextHostKey));
      if (!previousHost || previousHost !== nextMountEl) {
        await unmountLifecycle(previousLifecycle, previousCtx);
        clearHost(previousHost);
      }

      state.ctx = ctx;
      state.mounted = lifecycle;
      state.mountedTab = selected.manifest.id;
      state.activeTab = selected.manifest.id;
      state.activeTabName = normalizeStatusText(selected.manifest.name) || selected.manifest.id;
      scheduleTutorDeskLastUsedPage(selected.manifest.id);

      renderNavButtons(selected.manifest.id);

      root.classList.remove('is-switching');
      root.setAttribute('aria-busy', 'false');
      emitStatusIntent({
        pluginId: selected.manifest.id,
        routeKey: selected.manifest.id,
        routeEpoch: state.routeEpoch,
        opKey: routeStatusKey,
        opClass: 'route-transition',
        tone: 'ok',
        summary: `Ready: ${selected.manifest.name}`,
        busy: false,
        sticky: 'brief'
      });
      panelRouteActivity?.succeed();
      emitRuntimeHealth({
        status: 'healthy',
        pluginId: selected.manifest.id,
        tabId: selected.manifest.id,
        message: `Ready: ${selected.manifest.name}`,
        details: {
          pluginName: String(selected.manifest.name || '').trim() || selected.manifest.id
        }
      });
      scheduleIdlePrefetch();
    } catch (error) {
      console.error('[platform-shell] plugin load failed', error);
      if (nextCtx) await unmountLifecycle(nextLifecycle, nextCtx);
      if (destinationSkeletonVisible && previousHost) setVisibleHost(previousHostKey);
      clearTutorDeskDestinationSkeletonMarker(nextMountEl);
      clearHost(nextMountEl);
      root.classList.remove('is-switching');
      root.setAttribute('aria-busy', 'false');
      restoreRouteState(previousRouteState);
      renderNavButtons(state.activeTab);
      const recovery = await describePluginLoadError(error);
      const errorCode = recovery.code;
      const safeError = recovery.message;
      panelRouteActivity?.fail(safeError);
      emitRuntimeHealth({
        status: 'unhealthy',
        pluginId: selected.manifest.id,
        tabId: selected.manifest.id,
        message: 'Plugin load failed',
        details: {
          pluginName: String(selected.manifest.name || '').trim() || selected.manifest.id,
          errorCode,
          error: safeError
        }
      });
      if (previousLifecycle && previousHost) {
        await showPluginLoadRetryNotice(selected.manifest.id, selected.manifest.name, error);
        return;
      }
      state.mounted = null;
      state.mountedTab = '';
      state.ctx = null;
      showError('Plugin failed to load.', safeError);
    }
  }

  const registryItems = Object.values(pluginRegistry).map((entry) => ({
    manifest: entry && entry.manifest ? entry.manifest : {},
    module: entry && entry.module ? entry.module : null,
    load: entry && typeof entry.load === 'function' ? entry.load : null,
    prefetch: entry && typeof entry.prefetch === 'function' ? entry.prefetch : null,
    loading: String(entry?.loading || '').trim() || 'eager'
  }));

  const selectedItems = [];
  const validationErrors = [];

  const declaredPluginIds = resolveAppPluginIds(appValidation.appManifest);
  for (const pluginId of declaredPluginIds) {
    const found = registryItems.find((entry) => String(entry.manifest.id || '') === pluginId);
    if (!found) {
      validationErrors.push(`Plugin id '${pluginId}' is declared in app manifest but missing in registry.`);
      continue;
    }
    const validation = validatePluginManifest(found.manifest);
    if (!validation.ok) {
      validationErrors.push(`Plugin '${pluginId}' manifest invalid: ${validation.errors.join(' | ')}`);
      continue;
    }
    if (!isPluginApiCompatible(validation.manifest.apiVersion)) {
      validationErrors.push(
        `Plugin '${pluginId}' apiVersion ${validation.manifest.apiVersion} is incompatible with platform ${PLATFORM_PLUGIN_API_VERSION}.`
      );
      continue;
    }
    if (!found.module && typeof found.load !== 'function') {
      validationErrors.push(`Plugin '${pluginId}' registry entry must provide either module or load().`);
      continue;
    }
    selectedItems.push({
      manifest: validation.manifest,
      module: found.module,
      load: found.load,
      prefetch: found.prefetch,
      loading: found.loading
    });
  }

  if (validationErrors.length) {
    showError('App/plugin manifest validation failed.', validationErrors.join(' | '));
    setStatus('Manifest validation failed', 'err');
    throw new Error(validationErrors.join(' | '));
  }

  state.pluginItems = sortPlugins(selectedItems);
  publishRuntimeDebugHandle();

  function renderNavButtons(activeTabId = '') {
    const finalUiShellClassContract = document.documentElement?.getAttribute('data-kg-ui-standard') === TTORIA_SAAS_FINAL_UI_STANDARD
      && String(document.documentElement?.getAttribute('data-kg-ui-surfaces') || '').split(/\s+/u).includes('shell');
    const presentationOptions = finalUiShellClassContract
      ? { presentationMode: 'class-contract' }
      : {};
    const navItems = state.pluginItems
      .filter((item) => isPluginVisible(item));
    navHost.innerHTML = navItems.map((item) => {
      const active = String(activeTabId || '').trim() === item.manifest.id;
      return renderDsButton({
        label: item.manifest.tab.label,
        intent: active ? 'Primary' : 'Secondary',
        size: 'sm',
        state: active ? 'Active' : 'Default',
        className: active ? 'platform-shell-nav__button platform-shell-nav__button--active' : 'platform-shell-nav__button',
        ...presentationOptions,
        attrs: {
          'data-platform-tab': item.manifest.id,
          'data-kg-semantic-action-id': `platform-shell-nav|${item.manifest.id}`,
          'data-active': active ? 'true' : 'false'
        }
      });
    }).join('');
    const tutorDisplayName = resolveTutorDeskSettingsV2TutorDisplayName(auth.settingsV2, auth.userEmail);
    logoutHost.innerHTML = `${tutorDisplayName ? renderDsMessage({
      component: 'ds/shell-account-name',
      className: 'platform-shell-account-name',
      text: tutorDisplayName,
      attrs: {
        ...(!finalUiShellClassContract ? { title: tutorDisplayName } : {}),
        'data-testid': 'tutor-desk-personal-display-name'
      }
    }) : ''}${renderDsButton({
      label: finalUiShellClassContract ? 'Log out' : 'LOGOUT',
      intent: 'Secondary',
      size: 'sm',
      className: 'platform-shell-logout__button',
      ...presentationOptions,
      attrs: {
        'data-platform-action': 'logout',
        ...(logoutActivityCatalogEntry?.target?.id
          ? { 'data-kg-async-feedback-control': logoutActivityCatalogEntry.target.id }
          : {})
      }
    })}`;
  }

  renderNavButtons(state.activeTab);

  navHost.addEventListener('click', (event) => {
    const button = event.target.closest('[data-platform-tab]');
    if (!button) return;
    mobileDrawer.close();
    showTab(button.getAttribute('data-platform-tab'));
  });

  navHost.addEventListener('mouseenter', (event) => {
    const button = event.target.closest('[data-platform-tab]');
    if (!button) return;
    prefetchPluginTab(button.getAttribute('data-platform-tab'));
  }, true);

  navHost.addEventListener('focusin', (event) => {
    const button = event.target.closest('[data-platform-tab]');
    if (!button) return;
    prefetchPluginTab(button.getAttribute('data-platform-tab'));
  });

  logoutHost.addEventListener('click', (event) => {
    const actionButton = event.target.closest('[data-platform-action]');
    if (!actionButton || actionButton.getAttribute('data-platform-action') !== 'logout') return;
    event.preventDefault();
    const label = actionButton.querySelector('.ds-button__label');
    actionButton.disabled = true;
    actionButton.setAttribute('aria-busy', 'true');
    actionButton.setAttribute('aria-disabled', 'true');
    actionButton.setAttribute('data-kg-ui-state', 'busy');
    actionButton.setAttribute('data-tutor-desk-action-busy', 'true');
    if (logoutActivityCatalogEntry?.action) {
      actionButton.setAttribute('data-kg-async-feedback-action', logoutActivityCatalogEntry.action);
    }
    if (label) label.textContent = 'Signing out…';
    const logoutActivity = beginPlatformShellLogoutActivity();
    if (!logoutActivity) setStatus('Signing out...', 'warn');
    runtimeFetch('/auth/logout', {
      method: 'POST',
      cache: 'no-store'
    }).then((response) => {
      if (response?.ok === false) {
        logoutActivity?.fail(`The sign-out request returned status ${response.status}.`);
        return;
      }
      logoutActivity?.succeed();
    }).catch((error) => {
      logoutActivity?.fail(error?.message || 'The sign-out request could not be completed.');
    }).finally(() => {
      if (window && window.location && typeof window.location.assign === 'function') {
        window.location.assign('/login');
      }
    });
  });

  root.addEventListener('click', (event) => {
    const retryButton = event.target.closest('[data-platform-retry-tab]');
    if (retryButton) {
      event.preventDefault();
      showTab(retryButton.getAttribute('data-platform-retry-tab'));
      return;
    }
    const refreshButton = event.target.closest('[data-platform-refresh-app]');
    if (!refreshButton) return;
    event.preventDefault();
    window.location.reload();
  });

  const accessibleItems = state.pluginItems.filter((item) => isPluginAccessible(item));
  const visibleItems = accessibleItems.filter((item) => isPluginVisible(item));
  const preferredOpeningTab = resolveTutorDeskSettingsV2OpeningPluginId(auth.settingsV2);
  const initialTab = accessibleItems.some((item) => item.manifest.id === preferredOpeningTab)
    ? preferredOpeningTab
    : (accessibleItems.some((item) => item.manifest.id === appValidation.appManifest.defaultTab)
      ? appValidation.appManifest.defaultTab
      : (visibleItems[0]?.manifest.id || accessibleItems[0]?.manifest.id || ''));

  if (!initialTab) {
    showShellPlaceholder();
    setStatus('Ready: shell only', 'ok');
    return;
  }

  await showTab(initialTab);
}
